import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import MorpheusCountSessionDetail from "./MorpheusCountSessionDetail";
import "./mcSessions.css";
import { archiveSessionRecord, ensureCanonicalCountLedger, findSessionSeedById, loadArchivedSessionRecords, loadEngineState, loadSessionRecords, recoverMissingSessionRecord, resolveFlagConfig, resolveUnitRuleForPreparedRow, saveSessionRecords, saveSessionRecordsDurable } from "../utils/mcEngineData";
import { loadSites } from "../utils/mcOrgData";
import { getMcActiveOrgId, mcGetCloudItem, mcGetItem, mcSetCloudItem, mcSetCloudItemImmediate, mcSetCachedItem, mcSetItem, mcSubscribeCloudItem, mcTransitionCountLedger } from "../utils/mcCloudSync";
import { subscribeSessionListLive } from "../utils/mcLiveSessionSync";
import { buildProfileDisplayName, canFinalizeCount, filterRecordsForProfile, formatStoreLabel, getProfileAllowedSectionIds, isAdminProfile, loadProfiles, useAdminStoreView, useCurrentMcProfile } from "../utils/profileAccess";
import { activeSections, activeCycles, formatSectionLabel, normalizeSectionId, normalizeCycleId } from "../utils/mcSections";
import { getRememberedOperationalDate, getSessionOperationalDate, getSessionReviewTarget, installOperationalDateRefreshReset, rememberOperationalDate } from "../utils/mcSessionDateMemory";
import { clearLiveCountSaveQueueForTerminalSession } from "../utils/mcLiveCountSaveQueue";
import { hasZeroCostVarianceFlagBoundary, resolveCostVarianceFlagState } from "../utils/mcFlagging";


function isSessionSectionComplete(session = {}) {
  // A section is only complete after the user explicitly clicks Complete Section.
  // Fully counted lines mean "ready to complete", not completed/finalizable.
  if (session?.sectionCompletedAt || session?.meta?.sectionCompletedAt) return true;
  const status = String(session?.status || '').trim().toLowerCase();
  return status === 'section complete' || status === 'section-complete';
}


function isSessionVerified(session = {}) {
  return Boolean(
    session?.verifiedAt ||
    session?.verification?.verifiedAt ||
    String(session?.verificationStatus || session?.verification?.status || '').trim().toLowerCase() === 'verified'
  );
}

function isFinalizedHistoricalCount(session = {}) {
  const meta = session?.meta || {};
  const lifecycle = String(session?.lifecycleState || meta?.lifecycleState || "").trim().toLowerCase();
  const status = String(session?.status || session?.sessionStatus || meta?.status || "").trim().toLowerCase();
  const reason = String(session?.archiveReason || session?.reason || "").trim().toLowerCase();
  return Boolean(
    session?.finalizedAt
    || meta?.finalizedAt
    || lifecycle === "finalized"
    || status === "finalized"
    || reason.startsWith("finalized")
  );
}

function formatSessionStatusLabel(session = {}) {
  return isSessionSectionComplete(session) ? 'Complete' : (session?.status || 'Active');
}


function resolveCategoryScopeNames(session = {}) {
  const detailMeta = session?.detailSnapshot?.meta || {};
  const sources = [
    session?.countGroupNames,
    session?.meta?.countGroupNames,
    detailMeta?.countGroupNames,
    session?.categoryNames,
    session?.meta?.categoryNames,
    detailMeta?.categoryNames,
  ];
  for (const source of sources) {
    if (Array.isArray(source)) {
      const names = source.map((item) => String(item || '').trim()).filter(Boolean);
      if (names.length) return names;
    }
  }
  return [];
}

function resolveCategoryScopeLabel(session = {}) {
  const names = resolveCategoryScopeNames(session);
  const detailMeta = session?.detailSnapshot?.meta || {};
  return names.length
    ? names.join(' • ')
    : (session?.categoryScopeLabel || session?.meta?.categoryScopeLabel || detailMeta?.categoryScopeLabel || 'All Count Groups');
}
function categoryScopeKey(session = {}) {
  const names = resolveCategoryScopeNames(session);
  return names.length ? names.map((item) => String(item).trim().toLowerCase()).sort().join('|') : 'all';
}

function formatExposure(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: Math.abs(amount) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `-R${abs}` : `R${abs}`;
}


const detailStorageKeyFor = (sessionId) => `mc-count-ledger:${sessionId}`;
const legacyDetailStorageKeyFor = (sessionId) => `mc-session-engine:${sessionId}`;

function getCachedSessionDetail(sessionId) {
  return mcGetItem(detailStorageKeyFor(sessionId)) || mcGetItem(legacyDetailStorageKeyFor(sessionId));
}

function normalizeOwnershipCategory(value = '') {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function numericUnitValue(value, fallback = 1) {
  const parsed = Number(String(value ?? '').replace(/,/g, '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function resolveSessionLineCost(line = {}, detailMeta = {}, record = {}) {
  const scope = detailMeta?.storeKey || detailMeta?.storeName || detailMeta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL';
  const engine = loadEngineState(scope) || loadEngineState('ALL') || {};
  const config = engine?.config || {};
  const sourceCost = Number(line?.sourceCost ?? line?.importedCost ?? line?.originalCost ?? line?.cost ?? 0);
  const rule = resolveUnitRuleForPreparedRow(line?.product || '', line?.category || '', config);
  const priceUnitSize = numericUnitValue(rule?.priceUnitSize, numericUnitValue(line?.priceUnitSize, 1));
  return Number((rule?.priceBasis === 'base' ? sourceCost : sourceCost / priceUnitSize).toFixed(2));
}

function buildLineMetrics(lines = [], detailMeta = {}, record = {}) {
  let completedLines = 0;
  let flaggedCount = 0;
  let costExposure = 0;
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const touched = line?.counted !== '' || line?.waste !== '' || String(line?.note || '').trim();
    if (!touched) return;
    completedLines += 1;
    const counted = Number(line?.counted || 0);
    const waste = Number(line?.waste || 0);
    const systemSoh = Number(line?.systemSoh || 0);
    const variance = counted + waste - systemSoh;
    const persistedExposure = line?.costVariance ?? line?.finalCostVariance ?? line?.varianceValue;
    const exposure = persistedExposure !== null && persistedExposure !== undefined && persistedExposure !== '' && Number.isFinite(Number(persistedExposure))
      ? Number(Number(persistedExposure).toFixed(2))
      : Number((variance * resolveSessionLineCost(line, detailMeta, record)).toFixed(2));
    costExposure += exposure;
    const scope = detailMeta?.storeKey || detailMeta?.storeName || detailMeta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL';
    const flagConfig = resolveFlagConfig(scope);
    const noteFlagged = Boolean(String(line?.note || '').trim());
    const { negativeFlagged, positiveFlagged } = resolveCostVarianceFlagState(exposure, flagConfig);
    if (noteFlagged || negativeFlagged || positiveFlagged) flaggedCount += 1;
  });
  const totalLines = Array.isArray(lines) ? lines.length : 0;
  return {
    totalLines,
    completedLines,
    flaggedCount,
    costExposure: Number(costExposure.toFixed(2)),
    status: totalLines > 0 && completedLines >= totalLines ? 'Completed' : 'Active',
  };
}

function lineSectionId(line = {}, ownership = {}) {
  const direct = normalizeSectionId(line?.sectionId || line?.sectionName || '');
  if (direct && direct !== 'section') return direct;
  const category = normalizeOwnershipCategory(line?.category || line?.output || line?.normalizedOutput || line?.countGroup || line?.countGroupName || line?.product_category || line?.group || '');
  return normalizeSectionId(ownership?.[category] || ownership?.[line?.category] || ownership?.[line?.output] || ownership?.[line?.normalizedOutput] || ownership?.[line?.countGroup] || ownership?.[line?.countGroupName] || ownership?.[line?.product_category] || ownership?.[line?.group] || '');
}


function inferSectionFromDetailLines(lines = [], record = {}) {
  const existingId = normalizeSectionId(record?.sectionId || record?.sectionName || '');
  const existingName = String(record?.sectionName || '').trim();
  if (existingName && existingId !== 'section') return { sectionId: existingId, sectionName: existingName };

  const engine = loadEngineState(record?.storeKey || record?.storeName || record?.store || '') || loadEngineState() || {};
  const ownership = engine?.config?.categoryOwnership || {};
  const sections = activeSections();
  const sectionById = new Map(sections.map((section) => [normalizeSectionId(section?.sectionId || section?.id || section?.name), section]));
  const seen = new Set();

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const direct = normalizeSectionId(line?.sectionId || line?.sectionName || '');
    if (direct && direct !== 'section') {
      seen.add(direct);
      return;
    }
    const category = normalizeOwnershipCategory(line?.category || line?.output || line?.normalizedOutput || line?.countGroup || line?.countGroupName || line?.product_category || line?.group || '');
    const owner = normalizeSectionId(ownership?.[category] || ownership?.[line?.category] || ownership?.[line?.output] || ownership?.[line?.normalizedOutput] || ownership?.[line?.countGroup] || ownership?.[line?.countGroupName] || ownership?.[line?.product_category] || ownership?.[line?.group] || '');
    if (owner && owner !== 'section') seen.add(owner);
  });

  if (seen.size !== 1) return { sectionId: '', sectionName: '' };
  const sectionId = Array.from(seen)[0];
  const section = sectionById.get(sectionId);
  return { sectionId, sectionName: section?.name || formatSectionLabel(sectionId) };
}

function resolveProfileVisibleSessionMetrics(record = {}, detail = null, profile = {}) {
  if (!detail || !Array.isArray(detail?.lines)) return {};
  const sections = activeSections();
  const allowedIds = getProfileAllowedSectionIds(profile, sections).map(normalizeSectionId).filter(Boolean);

  // P4.1 — Sessions is a projection of the canonical Count Ledger.
  // Once a ledger is available, progress / flags / exposure are derived from its
  // rows only. The session-list record is discovery/routing metadata and must never
  // override operational facts, even for completed counts.
  if (isAdminProfile(profile) || allowedIds.includes('ALL')) {
    return buildLineMetrics(detail.lines, detail?.meta || {}, record);
  }

  const engine = loadEngineState(record?.storeKey || record?.storeName || record?.store || '') || loadEngineState() || {};
  const ownership = engine?.config?.categoryOwnership || {};
  const allowed = new Set(allowedIds);
  const visibleLines = detail.lines.filter((line) => allowed.has(lineSectionId(line, ownership)));
  return buildLineMetrics(visibleLines, detail?.meta || {}, record);
}


function normalizePersonName(value = '') {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function inferSectionFromProfileOwner(record = {}) {
  const sections = activeSections();
  const people = [record?.startedBy, record?.lastEditedBy, record?.createdBy, record?.updatedBy]
    .map(normalizePersonName)
    .filter(Boolean);
  if (!people.length || !sections.length) return { sectionId: '', sectionName: '' };

  const profiles = loadProfiles();
  const match = profiles.find((profile) => {
    const display = normalizePersonName(buildProfileDisplayName(profile));
    const firstLast = normalizePersonName(`${profile?.firstName || ''} ${profile?.lastName || ''}`);
    const candidates = [display, firstLast, normalizePersonName(profile?.name), normalizePersonName(profile?.displayName)].filter(Boolean);
    return candidates.some((candidate) => people.includes(candidate));
  });

  if (!match || isAdminProfile(match)) return { sectionId: '', sectionName: '' };
  const ids = getProfileAllowedSectionIds(match, sections).map(normalizeSectionId).filter((id) => id && id !== 'ALL');
  const unique = Array.from(new Set(ids));
  if (unique.length !== 1) return { sectionId: '', sectionName: '' };
  const sectionId = unique[0];
  const section = sections.find((item) => normalizeSectionId(item?.sectionId || item?.id || item?.name) === sectionId);
  return { sectionId, sectionName: section?.name || formatSectionLabel(sectionId) };
}

function cleanSectionFromSources(...sources) {
  const sections = activeSections();
  const sectionById = new Map(sections.map((section) => [normalizeSectionId(section?.sectionId || section?.id || section?.name), section]));
  for (const source of sources) {
    const id = normalizeSectionId(source?.sectionId || source?.section || source?.ownerSectionId || source?.countSectionId || source?.sectionName || source?.ownerSectionName || source?.countSectionName || '');
    const name = String(source?.sectionName || source?.section || source?.ownerSectionName || source?.countSectionName || '').trim();
    if (id && id !== 'section') {
      const section = sectionById.get(id);
      return { sectionId: id, sectionName: section?.name || name || formatSectionLabel(id) };
    }
    if (name) return { sectionId: normalizeSectionId(name), sectionName: name };
  }
  return { sectionId: '', sectionName: '' };
}

function sessionRecordsSignature(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => [
      item?.sessionId || '',
      item?.status || '',
      item?.sectionId || '',
      item?.sectionName || '',
      item?.fullSite === true ? 'full' : '',
      item?.sessionType || '',
      item?.countScope || '',
      item?.cycleId || '',
      item?.cycleName || '',
      item?.storeName || item?.store || item?.siteName || '',
      item?.completedLines || 0,
      item?.totalLines || 0,
      item?.flaggedCount || 0,
      item?.costExposure || 0,
      item?.updatedAt || item?.startedAt || '',
      item?.lastEditedBy || '',
      item?.verificationStatus || item?.verification?.status || '',
      item?.verifiedAt || item?.verification?.verifiedAt || '',
    ].join('|'))
    .join('::');
}


function isRicherSessionRecord(candidate = {}, fallback = {}) {
  const candidateScore =
    Number(candidate?.totalLines || 0) +
    Number(candidate?.completedLines || 0) +
    Number(candidate?.flaggedCount || 0) +
    Math.abs(Number(candidate?.costExposure || 0)) +
    (candidate?.sectionName ? 1 : 0) +
    (candidate?.cycleName ? 1 : 0) +
    (candidate?.startedBy ? 1 : 0);
  const fallbackScore =
    Number(fallback?.totalLines || 0) +
    Number(fallback?.completedLines || 0) +
    Number(fallback?.flaggedCount || 0) +
    Math.abs(Number(fallback?.costExposure || 0)) +
    (fallback?.sectionName ? 1 : 0) +
    (fallback?.cycleName ? 1 : 0) +
    (fallback?.startedBy ? 1 : 0);
  return candidateScore >= fallbackScore;
}

function mergeStableSessionRecords(previous = [], incoming = [], archivedIds = new Set()) {
  const prevById = new Map((Array.isArray(previous) ? previous : [])
    .filter((record) => record?.sessionId && !archivedIds.has(record.sessionId))
    .map((record) => [record.sessionId, record]));
  const nextById = new Map();

  (Array.isArray(incoming) ? incoming : []).forEach((record) => {
    if (!record?.sessionId || archivedIds.has(record.sessionId)) return;
    const previousRecord = prevById.get(record.sessionId);
    if (!previousRecord) {
      nextById.set(record.sessionId, record);
      return;
    }

    // P4.1: once either side is a canonical-ledger projection, generic list
    // richness/verification preservation is no longer allowed to reinterpret it.
    // Prefer the newest incoming ledger projection; otherwise keep the existing
    // ledger projection until its observer supplies the next canonical snapshot.
    if (record?.countAuthority === 'ledger' || previousRecord?.countAuthority === 'ledger') {
      nextById.set(record.sessionId, record?.countAuthority === 'ledger' ? record : previousRecord);
      return;
    }

    // Verification is a monotonic manager decision. A late Sessions list/detail
    // snapshot that was created before the Verify click must never roll it back to
    // Unverified. There is currently no automatic "unverify" lifecycle, so once
    // either side carries verifiedAt/status=verified we preserve that decision
    // while the durable Firestore write catches up.
    const verificationWasConfirmed = isSessionVerified(previousRecord) || isSessionVerified(record);
    const verifiedSource = isSessionVerified(record) ? record : previousRecord;
    const verificationPatch = verificationWasConfirmed ? {
      verificationStatus: 'verified',
      verifiedAt: verifiedSource?.verifiedAt || verifiedSource?.verification?.verifiedAt || previousRecord?.verifiedAt || record?.verifiedAt || '',
      verifiedBy: verifiedSource?.verifiedBy || verifiedSource?.verification?.verifiedBy || previousRecord?.verifiedBy || record?.verifiedBy || '',
      verifiedByProfileId: verifiedSource?.verifiedByProfileId || verifiedSource?.verification?.verifiedByProfileId || previousRecord?.verifiedByProfileId || record?.verifiedByProfileId || '',
      verification: {
        ...(previousRecord?.verification || {}),
        ...(record?.verification || {}),
        status: 'verified',
        verifiedAt: verifiedSource?.verifiedAt || verifiedSource?.verification?.verifiedAt || previousRecord?.verifiedAt || record?.verifiedAt || '',
        verifiedBy: verifiedSource?.verifiedBy || verifiedSource?.verification?.verifiedBy || previousRecord?.verifiedBy || record?.verifiedBy || '',
        verifiedByProfileId: verifiedSource?.verifiedByProfileId || verifiedSource?.verification?.verifiedByProfileId || previousRecord?.verifiedByProfileId || record?.verifiedByProfileId || '',
      },
    } : {};

    // Firestore/list-cache updates can arrive a split second before the
    // hydrated session detail/cache. Those lean records often have 0 progress
    // and make Sessions twitch between completed and empty. Keep the richer
    // operational snapshot while applying fresh routing/site metadata.
    if (isRicherSessionRecord(record, previousRecord)) {
      nextById.set(record.sessionId, { ...previousRecord, ...record, ...verificationPatch });
    } else {
      nextById.set(record.sessionId, { ...record, ...previousRecord, ...verificationPatch });
    }
  });

  // Keep previously visible active sessions when a transient storage/cloud sync
  // event sends a partial list. Explicit archive/finalize actions add ids to
  // archivedIds, so finalized sessions still disappear immediately.
  prevById.forEach((record, sessionId) => {
    if (!nextById.has(sessionId)) nextById.set(sessionId, record);
  });

  return Array.from(nextById.values());
}

function formatStartedAt(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
}

function isGenericFallbackSession(record) {
  const name = String(record?.storeName || record?.store || '').trim().toLowerCase();
  const file = String(record?.fileName || '').trim().toLowerCase();
  return name === 'the spot claremont' && file === 'prepared import' && Number(record?.totalLines || 0) <= 3;
}

function isStaleActiveSession(record) {
  if (!record || record.status === "Completed") return false;
  const dateValue = record.updatedAt || record.startedAt;
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const ageMs = Date.now() - date.getTime();
  const staleAfterMs = 36 * 60 * 60 * 1000;
  return ageMs > staleAfterMs && Number(record.completedLines || 0) === 0;
}

function storeKey(value = "") {
  return String(formatStoreLabel(value) || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function currentOrgSiteKeys() {
  return new Set(loadSites().filter((site) => site?.active !== false).map((site) => site?.siteId || site?.storeKey || site?.name).map(storeKey).filter(Boolean));
}

function recordBelongsToCurrentOrgSites(record = {}) {
  // The session list is read from the active organisation's Firestore document and
  // hydrateRecords already enforces an exact tenantOrgId/orgId match. The Sites
  // registry is descriptive metadata only and may still be hydrating (or contain a
  // partial row-site manifest), so it must never hide an org-authorised live count.
  const activeOrgId = String(getMcActiveOrgId() || "").trim();
  const recordOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
  if (activeOrgId && recordOrgId === activeOrgId) return true;
  if (Number(record?.tenantProofVersion || 0) >= 2) return true;

  const known = currentOrgSiteKeys();
  const candidates = [record?.storeName, record?.store, record?.siteName, record?.storeKey, record?.siteId].map(storeKey).filter(Boolean);
  const concrete = candidates.filter((key) => key !== "all" && key !== "allsites");

  // Legacy rows do not have the stronger tenant-proof marker, so retain the site
  // registry guard for them. While the registry is unavailable, keep the record
  // visible rather than creating a false empty Sessions screen.
  if (!known.size) return true;
  if (!concrete.length) return true;
  return concrete.some((key) => known.has(key));
}

function normalizeActiveSessionRecord(record) {
  if (!record || record.archived === true || record.archivedAt || record.deletedAt) return null;
  const status = String(record.status || "").trim().toLowerCase();
  if (status === "archived" || status === "archive") return null;
  if (isGenericFallbackSession(record)) return null;
  // Do not auto-hide active sessions because they are old or still at 0 progress.
  // Managers may prepare a count and return later; Sessions must remain the
  // operational source of truth until archive/finalize removes it.
  return { ...record, archived: false };
}

function resolveSessionRecord(record, currentProfile, canonicalRawOverride = null) {
  if (!record?.sessionId) return record;
  const fallbackStartedBy = record?.startedBy === "Nelisiwe"
    ? (buildProfileDisplayName(currentProfile) || record?.startedBy || "User")
    : (record?.startedBy || "User");
  try {
    const raw = canonicalRawOverride || getCachedSessionDetail(record.sessionId);
    if (!raw) {
      const seed = findSessionSeedById(record.sessionId) || {};
      const profileSection = inferSectionFromProfileOwner({ ...record, ...seed, startedBy: fallbackStartedBy });
      const directSection = cleanSectionFromSources(seed, record, profileSection);
      const fullSite = seed?.fullSite === true || record?.fullSite === true || String(seed?.countScope || seed?.sessionType || record?.countScope || record?.sessionType || '').toLowerCase().includes('full');
      return {
        ...record,
        sectionId: fullSite ? '' : (directSection.sectionId || seed?.sectionId || record?.sectionId || ''),
        sectionName: fullSite ? '' : (directSection.sectionName || seed?.sectionName || record?.sectionName || ''),
        fullSite,
        isFullSite: fullSite,
        fullCount: fullSite,
        sessionType: seed?.sessionType || record?.sessionType || (fullSite ? 'full-count' : 'section'),
        countScope: seed?.countScope || record?.countScope || (fullSite ? 'full-site' : 'section'),
        cycleId: seed?.cycleId || seed?.cycleKey || seed?.cycle?.cycleId || seed?.cycle?.id || record?.cycleId || record?.cycleKey || record?.cycle?.cycleId || record?.cycle?.id || '',
        cycleName: seed?.cycleName || seed?.cycle?.cycleName || seed?.cycle?.name || record?.cycleName || record?.cycle?.cycleName || record?.cycle?.name || '',
        cycle: seed?.cycle || record?.cycle || undefined,
        startedBy: seed?.startedBy || fallbackStartedBy,
        lastEditedBy: seed?.lastEditedBy || record?.lastEditedBy || fallbackStartedBy,
        sectionCompletedAt: seed?.sectionCompletedAt || record?.sectionCompletedAt || null,
        status: (seed?.sectionCompletedAt || record?.sectionCompletedAt || ['Section Complete', 'section-complete'].includes(seed?.status) || ['Section Complete', 'section-complete'].includes(record?.status))
          ? 'Section Complete'
          : 'Active',
      };
    }
    const parsed = JSON.parse(raw);
    const meta = parsed?.meta || {};
    const resolvedStartedBy = (meta.startedBy === "Nelisiwe" ? (buildProfileDisplayName(currentProfile) || meta.startedBy) : (meta.startedBy || fallbackStartedBy));
    const derivedMetrics = resolveProfileVisibleSessionMetrics(record, parsed, currentProfile);
    const ledgerCompleted = Boolean(
      meta.sectionCompletedAt ||
      meta.completedAt ||
      String(meta.status || '').trim().toLowerCase() === 'section complete' ||
      String(meta.lifecycleState || '').trim().toLowerCase() === 'section-complete' ||
      String(meta.lifecycleState || '').trim().toLowerCase() === 'complete'
    );
    const ledgerVerified = Boolean(
      meta.verifiedAt ||
      meta.verification?.verifiedAt ||
      String(meta.verificationStatus || meta.verification?.status || '').trim().toLowerCase() === 'verified'
    );
    const ledgerSummary = meta?.sessionSummary && typeof meta.sessionSummary === 'object' ? meta.sessionSummary : null;
    const liveLedgerSummary = meta?.liveSessionSummary && typeof meta.liveSessionSummary === 'object' ? meta.liveSessionSummary : null;
    const isLegacyDataLossLedger = Boolean(meta?.dataLoss) && (!Array.isArray(parsed?.lines) || parsed.lines.length === 0);
    const historicalMetrics = isLegacyDataLossLedger
      ? {
          ...derivedMetrics,
          // MC467 — a terminal-summary recovery ledger intentionally has no rows.
          // Verification must never make Sessions forget the already-known 101/101
          // (or flags/exposure) just because lifecycle truth now comes from that
          // zero-row ledger. Preserve the historical summary/read-model metrics while
          // still taking verification state exclusively from the canonical ledger.
          totalLines: Number(meta?.historicalTotalLines ?? record?.totalLines ?? derivedMetrics.totalLines ?? 0),
          completedLines: Number(meta?.historicalCompletedLines ?? record?.completedLines ?? derivedMetrics.completedLines ?? 0),
          flaggedCount: Number(meta?.historicalFlaggedCount ?? record?.flaggedCount ?? derivedMetrics.flaggedCount ?? 0),
          costExposure: Number(meta?.historicalCostExposure ?? record?.costExposure ?? derivedMetrics.costExposure ?? 0),
        }
      : null;
    // MC482 — the live Count Ledger already carries the exact running sessionSummary
    // calculated by the count-detail page on every persisted live snapshot. For an
    // admin / ALL-sections observer, consume that summary while the count is Active
    // as well as after completion. Recalculating exposure from the lean ledger rows
    // can lose Unit Intelligence cost context and incorrectly show R0 in Sessions.
    // Section-restricted profiles still derive only their visible rows below.
    const canSeeWholeSession = isAdminProfile(currentProfile) || getProfileAllowedSectionIds(currentProfile, activeSections()).map(normalizeSectionId).includes('ALL');
    // MC483 — sessionSummary is intentionally terminal-only. During an Active count,
    // use the liveSessionSummary embedded in the same Count Ledger write. This gives
    // Sessions the exact monetary calculation from the counter without adding any
    // Firestore reads, writes, polling, or listeners.
    const authoritativeSummary = ledgerCompleted ? ledgerSummary : liveLedgerSummary;
    const scopedMetrics = historicalMetrics || (authoritativeSummary && canSeeWholeSession
      ? {
          ...derivedMetrics,
          totalLines: Number(authoritativeSummary.totalLines ?? derivedMetrics.totalLines ?? 0),
          completedLines: Number(authoritativeSummary.completedLines ?? derivedMetrics.completedLines ?? 0),
          flaggedCount: hasZeroCostVarianceFlagBoundary(resolveFlagConfig(meta?.storeKey || meta?.storeName || meta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL'))
            ? Number(derivedMetrics.flaggedCount || 0)
            : Number(authoritativeSummary.flaggedCount ?? authoritativeSummary.flaggedItems ?? authoritativeSummary.flaggedProducts ?? derivedMetrics.flaggedCount ?? 0),
          costExposure: Number(authoritativeSummary.costExposure ?? authoritativeSummary.exposure ?? authoritativeSummary.finalExposure ?? derivedMetrics.costExposure ?? 0),
        }
      : derivedMetrics);
    const inferredSection = inferSectionFromDetailLines(parsed?.lines || [], { ...record, ...meta });
    const profileSection = inferSectionFromProfileOwner({ ...record, ...meta, startedBy: resolvedStartedBy });
    const directSection = cleanSectionFromSources(meta, record, inferredSection, profileSection);
    const resolvedFullSite = meta.fullSite === true || record?.fullSite === true || String(meta.countScope || record?.countScope || meta.sessionType || record?.sessionType || '').toLowerCase().includes('full');
    return {
      ...record,
      ...scopedMetrics,
      // Lifecycle facts come from the canonical ledger only. 100% row progress is
      // still merely "ready to complete" until the ledger carries completion.
      status: ledgerCompleted ? 'Section Complete' : 'Active',
      sectionCompletedAt: meta.sectionCompletedAt || meta.completedAt || null,
      verificationStatus: ledgerVerified ? 'verified' : 'unverified',
      verifiedAt: ledgerVerified ? (meta.verifiedAt || meta.verification?.verifiedAt || '') : '',
      verifiedBy: ledgerVerified ? (meta.verifiedBy || meta.verification?.verifiedBy || '') : '',
      verifiedByProfileId: ledgerVerified ? (meta.verifiedByProfileId || meta.verification?.verifiedByProfileId || '') : '',
      verification: {
        ...(meta.verification || {}),
        status: ledgerVerified ? 'verified' : 'unverified',
        verifiedAt: ledgerVerified ? (meta.verifiedAt || meta.verification?.verifiedAt || '') : '',
        verifiedBy: ledgerVerified ? (meta.verifiedBy || meta.verification?.verifiedBy || '') : '',
        verifiedByProfileId: ledgerVerified ? (meta.verifiedByProfileId || meta.verification?.verifiedByProfileId || '') : '',
      },
      countAuthority: 'ledger',
      sectionId: resolvedFullSite ? '' : (directSection.sectionId || ''),
      sectionName: resolvedFullSite ? '' : (directSection.sectionName || ''),
      fullSite: resolvedFullSite,
      sessionType: meta.sessionType || record?.sessionType || (resolvedFullSite ? 'full-count' : 'section'),
      countScope: meta.countScope || record?.countScope || (resolvedFullSite ? 'full-site' : 'section'),
      cycleId: meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id || record?.cycleId || record?.cycleKey || record?.cycle?.cycleId || record?.cycle?.id || '',
      cycleName: meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || record?.cycleName || record?.cycle?.cycleName || record?.cycle?.name || '',
      startedBy: resolvedStartedBy,
      lastEditedBy: meta.lastEditedBy || record?.lastEditedBy || resolvedStartedBy,
      correctedAt: meta.correctedAt || record?.correctedAt || '',
      correctedBy: meta.correctedBy || record?.correctedBy || '',
      correctionVersions: Array.isArray(meta.correctionVersions)
        ? meta.correctionVersions
        : (Array.isArray(record?.correctionVersions) ? record.correctionVersions : []),
      startedAt: meta.startedAt || record.startedAt,
      operationalDate: meta.operationalDate || meta.businessDate || meta.countDate || record.operationalDate || record.businessDate || record.countDate || '',
      updatedAt: meta.lastEditedAt || parsed?.savedAt || record.updatedAt,
    };
  } catch {
    return record;
  }
}


function resolveSessionSiteName(session = {}) {
  return String(session?.storeName || session?.store || session?.siteName || session?.site || 'Unknown Site').trim() || 'Unknown Site';
}

function resolveSessionCycle(session = {}) {
  const cycles = activeCycles();
  const rawId = String(session?.cycleId || session?.cycleKey || session?.cycle?.cycleId || session?.cycle?.id || session?.meta?.cycleId || session?.meta?.cycleKey || '').trim();
  const rawName = String(session?.cycleName || session?.cycle?.cycleName || session?.cycle?.name || session?.meta?.cycleName || session?.meta?.cycle?.name || '').trim();

  // Cycle IDs are the durable identity. A cycle can be renamed and its old name
  // can later be reused by a newly created cycle, so mixing the saved cycle name
  // into ID matching can attach a new session to the wrong configured cycle.
  // Always resolve an explicit saved ID against configured IDs first. Only use a
  // display-name fallback for legacy sessions that genuinely have no cycle ID.
  const normalizedRawId = rawId ? normalizeCycleId(rawId) : '';
  const matchedById = normalizedRawId
    ? cycles.find((cycle) => [cycle?.cycleId, cycle?.id]
        .map(normalizeCycleId)
        .filter(Boolean)
        .includes(normalizedRawId))
    : null;
  const normalizedRawName = rawName ? normalizeCycleId(rawName) : '';
  const matchedByName = !normalizedRawId && normalizedRawName
    ? cycles.find((cycle) => normalizeCycleId(cycle?.name || '') === normalizedRawName)
    : null;
  const matched = matchedById || matchedByName;
  const singleActiveCycle = cycles.length === 1 ? cycles[0] : null;
  const cycle = matched || (rawId || rawName
    ? { cycleId: rawId || normalizeCycleId(rawName), name: rawName || rawId }
    : singleActiveCycle
      ? singleActiveCycle
      : { cycleId: `uncycled-${session?.sessionId || 'session'}`, name: 'Unassigned Cycle', description: 'This session is missing cycle metadata.' });
  return {
    cycleId: String(cycle?.cycleId || cycle?.id || rawId || `uncycled-${session?.sessionId || 'session'}`).trim() || `uncycled-${session?.sessionId || 'session'}`,
    cycleName: String(cycle?.name || rawName || 'Unassigned Cycle').trim() || 'Unassigned Cycle',
    description: String(cycle?.description || '').trim(),
  };
}

function resolveSessionSectionName(session = {}) {
  const sectionId = normalizeSectionId(session?.sectionId || '');
  const sectionName = String(session?.sectionName || '').trim();
  const type = String(session?.countScope || session?.sessionType || session?.type || '').toLowerCase();
  const isFullSite = session?.fullSite === true || session?.isFullSite === true || session?.fullCount === true || type.includes('full') || ['all', 'all-sections'].includes(type);
  if (isFullSite) return 'Full Count';
  // Older bad records saved full-site counts as sectionId/sectionName = "section" with the full item total.
  if ((!sectionName || sectionId === 'section') && Number(session?.totalLines || 0) > 50) return 'Full Count';
  if (sectionName && sectionId !== 'section') return sectionName;
  return formatSectionLabel(session?.sectionId || '') || 'Section';
}

function buildGroupedSessions(sessions = []) {
  const sites = new Map();
  (Array.isArray(sessions) ? sessions : []).forEach((session) => {
    const siteName = resolveSessionSiteName(session);
    const cycle = resolveSessionCycle(session);
    if (!sites.has(siteName)) {
      sites.set(siteName, { siteName, cycles: new Map() });
    }
    const siteGroup = sites.get(siteName);
    if (!siteGroup.cycles.has(cycle.cycleId)) {
      siteGroup.cycles.set(cycle.cycleId, { ...cycle, sessions: [] });
    }
    siteGroup.cycles.get(cycle.cycleId).sessions.push(session);
  });

  return Array.from(sites.values())
    .sort((a, b) => a.siteName.localeCompare(b.siteName))
    .map((site) => ({
      ...site,
      cycles: Array.from(site.cycles.values())
        .map((cycleGroup) => {
          // Firestore snapshots and cross-device merges do not guarantee array order.
          // Always derive a deterministic section order so rows never visually swap
          // when the same session data is emitted in a different sequence.
          const configuredSections = activeSections();
          const sectionOrder = new Map(
            configuredSections.map((section, index) => [
              normalizeSectionId(section?.sectionId || section?.id || section?.name || ''),
              index,
            ])
          );
          const sessions = [...cycleGroup.sessions].sort((a, b) => {
            const sectionA = normalizeSectionId(resolveSessionSectionName(a));
            const sectionB = normalizeSectionId(resolveSessionSectionName(b));
            const indexA = sectionOrder.has(sectionA) ? sectionOrder.get(sectionA) : 9999;
            const indexB = sectionOrder.has(sectionB) ? sectionOrder.get(sectionB) : 9999;
            if (indexA !== indexB) return indexA - indexB;
            const nameOrder = resolveSessionSectionName(a).localeCompare(resolveSessionSectionName(b));
            if (nameOrder !== 0) return nameOrder;
            return String(a?.sessionId || '').localeCompare(String(b?.sessionId || ''));
          });
          return { ...cycleGroup, sessions };
        })
        .sort((a, b) => {
          const cycles = activeCycles();
          const indexA = cycles.findIndex((cycle) => String(cycle?.cycleId || cycle?.id) === String(a.cycleId));
          const indexB = cycles.findIndex((cycle) => String(cycle?.cycleId || cycle?.id) === String(b.cycleId));
          if (indexA !== indexB) return (indexA < 0 ? 9999 : indexA) - (indexB < 0 ? 9999 : indexB);
          return String(a.cycleName || '').localeCompare(String(b.cycleName || ''));
        }),
    }));
}


function latestCycleExposureForSite(siteGroup = {}) {
  const cycles = Array.isArray(siteGroup?.cycles) ? siteGroup.cycles : [];
  if (!cycles.length) return 0;
  // buildGroupedSessions already sorts cycles by the configured operational order.
  // Exposure is a stock position, not an additive movement metric: use the latest
  // cycle's position for the site instead of summing historical cycles together.
  const latestCycle = cycles[cycles.length - 1];
  return (Array.isArray(latestCycle?.sessions) ? latestCycle.sessions : [])
    .reduce((sum, session) => sum + Number(session?.costExposure || 0), 0);
}

function sessionDateKey(session = {}) {
  return getSessionOperationalDate(session) || 'Undated';
}

function formatOperationalDate(value = '') {
  if (value === 'Undated') return 'Undated sessions';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function buildSessionDisplayName(session = {}) {
  const site = String(session?.storeName || session?.store || session?.siteName || '').trim();
  const section = String(session?.sectionName || '').trim();
  const isFullSite = session?.fullSite === true || session?.isFullSite === true || session?.fullCount === true || String(session?.countScope || session?.sessionType || session?.type || '').toLowerCase().includes('full');
  if (site && isFullSite) return `${site} - Full Count`;
  if (site && section) return `${site} - ${section}`;
  if (section) return section;
  if (site) return site;
  return 'Count Session';
}

function formatProgress(record) {
  const total = Number(record.totalLines || 0);
  const done = Number(record.completedLines || 0);
  if (!total) return 0;
  return Math.round((done / total) * 100);
}


function sessionBelongsToActiveTenant(record = {}, activeOrgId = "") {
  const expected = String(activeOrgId || "").trim();
  const stamped = String(record?.tenantOrgId || record?.orgId || "").trim();
  return Boolean(expected && stamped && stamped === expected);
}

const SESSION_QUARANTINE_KEY = "mc-session-tenant-quarantine";

function normalizeSku(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function readSessionQuarantineIds() {
  try {
    const parsed = JSON.parse(mcGetItem(SESSION_QUARANTINE_KEY) || "[]");
    return new Set((Array.isArray(parsed) ? parsed : []).map((item) => String(item || "").trim()).filter(Boolean));
  } catch (_) {
    return new Set();
  }
}

function persistSessionQuarantineIds(ids = new Set()) {
  const value = JSON.stringify(Array.from(ids).filter(Boolean).sort());
  mcSetItem(SESSION_QUARANTINE_KEY, value);
  mcSetCloudItem(SESSION_QUARANTINE_KEY, value).catch(() => {});
}

function legacySessionMatchesCurrentInventory(record = {}) {
  // Sessions created by the tenant-proof write path are safe after the strict
  // tenantOrgId check above. Older rows may have been copied into another org's
  // session-list and later stamped during a merge, so the stamp alone is not proof.
  if (Number(record?.tenantProofVersion || 0) >= 2) return true;

  let detail = null;
  try { detail = JSON.parse(getCachedSessionDetail(record?.sessionId) || "null"); } catch (_) {}
  const detailLines = Array.isArray(detail?.lines) ? detail.lines : [];

  // The active session-list itself is already read from the current organisation
  // document and the summary must carry the matching tenantOrgId. A legacy row may
  // legitimately reach Sessions before its detail payload has warmed into the
  // browser cache after refresh. Treat that state as "not yet verifiable" rather
  // than cross-org evidence; otherwise every valid legacy live session is hidden
  // while Archives (which uses a different list) continues to populate.
  if (!detailLines.length) return null;

  const scope = record?.storeKey || record?.storeName || record?.store || detail?.meta?.storeKey || detail?.meta?.storeName || "ALL";
  const engine = loadEngineState(scope) || loadEngineState("ALL") || {};
  const inventoryRows = Array.isArray(engine?.preparedRows) && engine.preparedRows.length
    ? engine.preparedRows
    : (Array.isArray(engine?.rawRows) ? engine.rawRows : []);

  // Inventory hydration is also asynchronous on routes such as Sessions. Absence
  // here is not evidence that the tenant-stamped session belongs elsewhere.
  if (!inventoryRows.length) return null;

  const inventorySkus = new Set(inventoryRows.map((row) => normalizeSku(row?.sku || row?.SKU || row?.barcode || row?.id)).filter(Boolean));
  const sessionSkus = Array.from(new Set(detailLines.map((row) => normalizeSku(row?.sku || row?.SKU || row?.barcode || row?.id)).filter(Boolean)));
  if (!sessionSkus.length || !inventorySkus.size) return false;

  const overlap = sessionSkus.filter((sku) => inventorySkus.has(sku)).length;
  return overlap / sessionSkus.length >= 0.5;
}



function durableOperationalValue(value = "") {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function durableOperationalDate(record = {}) {
  const explicit = String(
    record?.operationalDate || record?.businessDate || record?.countDate || ""
  ).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;

  const idMatch = String(record?.sessionId || record?.id || "").match(/^MC-(\d{2})(\d{2})(\d{2})(?:-|$)/i);
  if (idMatch) return `20${idMatch[1]}-${idMatch[2]}-${idMatch[3]}`;

  const raw = record?.startedAt || record?.createdAt || record?.updatedAt || record?.archivedAt || "";
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function durableOperationalKey(record = {}) {
  const date = durableOperationalDate(record);
  const site = durableOperationalValue(
    record?.storeKey || record?.siteId || record?.storeName || record?.store || record?.siteName
  );
  const cycle = durableOperationalValue(
    record?.cycleId || record?.cycleKey || record?.cycle?.cycleId ||
    record?.cycle?.id || record?.cycleName || record?.cycle?.name
  );
  const isFull =
    record?.fullSite === true || record?.isFullSite === true || record?.fullCount === true ||
    durableOperationalValue(record?.sessionType || record?.countScope).includes("full");
  const section = isFull
    ? "full-count"
    : durableOperationalValue(
        record?.sectionId || record?.ownerSectionId || record?.countSectionId ||
        record?.sectionName || record?.ownerSectionName || record?.countSectionName || "section"
      );
  if (!date || !site) return "";
  return [date, site, cycle, section].join("::");
}

function durableRecordTime(record = {}) {
  const raw =
    record?.restoredAt || record?.updatedAt || record?.completedAt ||
    record?.sectionCompletedAt || record?.startedAt || record?.createdAt || "";
  const parsed = Date.parse(raw || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function MorpheusCountSessionsInner() {
  const location = useLocation();
  const sessionReviewTarget = useMemo(() => getSessionReviewTarget(location.search), [location.search]);
  const reviewNavigationKey = `${location.key || ''}:${location.search || ''}`;
  const reviewDateAppliedRef = useRef('');
  const reviewScrolledRef = useRef('');
  const currentProfile = useCurrentMcProfile();
  const navigate = useNavigate();
  const adminStoreView = useAdminStoreView();
  const [records, setRecords] = useState(() => loadSessionRecords().map((record) => normalizeActiveSessionRecord(resolveSessionRecord(record, currentProfile))).filter(Boolean));
  const [sessionsHydrated, setSessionsHydrated] = useState(false);
  const [sessionsSyncing, setSessionsSyncing] = useState(true);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmFinalize, setConfirmFinalize] = useState(null);
  const [recoverSessionId, setRecoverSessionId] = useState("");
  const [recoverStatus, setRecoverStatus] = useState("");
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [archiveBusyId, setArchiveBusyId] = useState("");
  const [finalizeBusyId, setFinalizeBusyId] = useState("");
  const [verifyBusyId, setVerifyBusyId] = useState("");
  const [confirmVerify, setConfirmVerify] = useState(null);
  const [bulkArchiveTarget, setBulkArchiveTarget] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => sessionReviewTarget.date || getRememberedOperationalDate() || sessionDateKey({ startedAt: new Date().toISOString() }));
  const [collapsedSites, setCollapsedSites] = useState(() => new Set());
  const [compareSessionIds, setCompareSessionIds] = useState(() => new Set());
  const [compareStatus, setCompareStatus] = useState("");
  const compareTrayRef = useRef(null);
  const compareHistorySelectRef = useRef(null);
  const [openingSession, setOpeningSession] = useState(null);
  const openingTimerRef = useRef(null);
  const detailWarmupRef = useRef(new Map());
  const recordsSignatureRef = useRef("");
  const stableRecordsRef = useRef([]);
  const archivedSessionIdsRef = useRef(new Set());
  const hydratedRef = useRef(false);

  const [, setCloudSyncRevision] = useState(0);
  const isAdmin = isAdminProfile(currentProfile);
  const canFinalize = canFinalizeCount(currentProfile);

  // Session hydration can mount before mcCloudSync has finished resolving the
  // active organisation. getMcActiveOrgId() is module state, so that transition
  // does not itself cause this component to re-render. After a cold cache/reset
  // the page could therefore start with an empty org id, clear its records, and
  // remain permanently at "0 sessions" until some unrelated UI update happened.
  //
  // Re-render when cloud sync reports readiness, with a short one-shot probe as
  // a safety net for the narrow race where the ready event fired just before this
  // component attached its listener. This does not read or write Firestore.
  useEffect(() => {
    let probeTimer = null;
    const refreshOrgScope = () => setCloudSyncRevision((value) => value + 1);

    window.addEventListener("mc-cloud-sync-ready", refreshOrgScope);

    const probe = () => {
      if (String(getMcActiveOrgId() || "").trim()) {
        refreshOrgScope();
        if (probeTimer) {
          window.clearInterval(probeTimer);
          probeTimer = null;
        }
      }
    };

    probe();
    if (!String(getMcActiveOrgId() || "").trim()) {
      probeTimer = window.setInterval(probe, 250);
    }

    return () => {
      window.removeEventListener("mc-cloud-sync-ready", refreshOrgScope);
      if (probeTimer) window.clearInterval(probeTimer);
    };
  }, []);
  // useCurrentMcProfile returns a fresh object whenever any storage event fires.
  // Live session sync intentionally emits storage-compatible events, so depending
  // on the object identity here caused this hydration effect to tear down and
  // restart continuously: Sessions alternated between “Loading live sessions…”
  // and the real list. Only restart when the active tenant/profile identity or
  // access scope actually changes.
  // Session hydration is tenant-scoped. Profile objects are rebuilt by the storage
  // bridge and may temporarily contain partially hydrated arrays; using those arrays
  // as an effect dependency repeatedly reset the screen to “Loading live sessions…”.
  // Access filtering still uses currentProfile during hydration, but the Firestore
  // listener lifecycle changes only when the actual tenant or identity changes.
  const activeSessionOrgId = String(getMcActiveOrgId() || '').trim();
  // The live-session listener belongs to the organisation, not the selected
  // operator profile. Profile hydration can legitimately change several times
  // after login (temporary name/email record -> resolved profile record). Tying
  // the listener lifecycle to that profile identity tears down the listener and
  // flashes the loading state on every profile refresh. Access filtering is still
  // applied while normalising rows, but listener ownership changes only when the
  // active organisation changes.
  const sessionHydrationScopeKey = activeSessionOrgId;

  useEffect(() => {
    let mounted = true;
    let hydrateTimer = null;
    const liveDetailUnsubscribers = new Map();
    const liveDetailSignatures = new Map();
    let canonicalDiscoveryReady = false;
    let deferredEmptyLiveRecords = null;
    const effectOrgId = String(getMcActiveOrgId() || "").trim();

    // Never turn a transient "org not resolved yet" startup state into an
    // authoritative empty Sessions screen. The cloud-sync-ready listener above
    // re-renders this component as soon as the real organisation is available,
    // at which point this tenant-scoped hydration effect starts normally.
    if (!effectOrgId) {
      setSessionsHydrated(false);
      setSessionsSyncing(true);
      return () => {
        mounted = false;
      };
    }

    // A Sessions component can survive an organisation switch while React keeps the
    // same route mounted. Never carry the previous organisation's stable merge cache
    // into the next organisation. The Firestore document for the active org is the
    // only permitted bootstrap source for this page.
    stableRecordsRef.current = [];
    archivedSessionIdsRef.current = new Set();
    recordsSignatureRef.current = "";
    hydratedRef.current = false;
    setRecords([]);
    setSessionsHydrated(false);
    setSessionsSyncing(true);

    const hydrateRecords = (sourceRecords) => {
      const baseRecords = Array.isArray(sourceRecords) ? sourceRecords : [];
      const quarantined = readSessionQuarantineIds();
      let quarantineChanged = false;
      const hydrated = baseRecords
        .filter((record) => sessionBelongsToActiveTenant(record, effectOrgId))
        .filter((record) => {
          const id = String(record?.sessionId || "").trim();
          if (!id) return false;
          const ownershipCheck = legacySessionMatchesCurrentInventory(record);
          if (ownershipCheck !== false) {
            // Reconsider IDs quarantined by the old startup race. A null result
            // means detail/inventory has not hydrated yet, while the strict
            // tenantOrgId check above has already established the active tenant.
            if (quarantined.delete(id)) quarantineChanged = true;
            return true;
          }
          if (!quarantined.has(id)) {
            quarantined.add(id);
            quarantineChanged = true;
          }
          console.warn("Morpheus Count quarantined a cross-organisation legacy session", { sessionId: id, orgId: effectOrgId });
          return false;
        })
        .map((record) => normalizeActiveSessionRecord(resolveSessionRecord(record, currentProfile)))
        .filter(Boolean);
      if (quarantineChanged) persistSessionQuarantineIds(quarantined);
      return hydrated;
    };

    const applyLiveLedgerSnapshot = (sessionId, raw) => {
      if (!mounted || !raw || String(getMcActiveOrgId() || '').trim() !== effectOrgId) return;
      if (liveDetailSignatures.get(sessionId) === raw) return;
      liveDetailSignatures.set(sessionId, raw);
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed?.lines) || !parsed.lines.length) return;

        // Sessions is a read-only operator observer. Mirror the canonical Count
        // Ledger into the browser cache and replace only this session's live metrics.
        // This bypasses the generic stable-list merge that intentionally protects
        // session visibility but must never pin stale progress/exposure.
        mcSetCachedItem(detailStorageKeyFor(sessionId), raw);

        const currentRecords = Array.isArray(stableRecordsRef.current)
          ? stableRecordsRef.current
          : [];
        const targetIndex = currentRecords.findIndex((record) => String(record?.sessionId || '') === sessionId);
        if (targetIndex < 0) return;

        const currentRecord = currentRecords[targetIndex];
        const resolvedRecord = normalizeActiveSessionRecord(resolveSessionRecord(currentRecord, currentProfile, raw));
        if (!resolvedRecord) return;

        // P4.1: never preserve a competing list/session interpretation here. The
        // canonical ledger snapshot alone decides lifecycle + operational metrics.
        const nextRecord = resolvedRecord;

        const nextRecords = [...currentRecords];
        nextRecords[targetIndex] = nextRecord;
        stableRecordsRef.current = nextRecords;
        const nextSignature = sessionRecordsSignature(nextRecords);
        if (recordsSignatureRef.current !== nextSignature) {
          recordsSignatureRef.current = nextSignature;
          setRecords(nextRecords);
        }
      } catch (_) {}
    };

    // COST C2 — per-session Count Ledgers already use Firestore document listeners.
    // Do not layer a 60-second get() safety poll on top of those listeners: Firestore
    // reconnects document listeners automatically, and repeated fallback reads paid
    // for unchanged ledgers. Live data remains realtime while this Sessions screen is
    // mounted, with zero recurring polling reads.

    const syncLiveDetailSubscriptions = (sourceRecords = []) => {
      const wantedIds = new Set((Array.isArray(sourceRecords) ? sourceRecords : [])
        .filter((record) => record?.sessionId && (!isSessionSectionComplete(record) || !isSessionVerified(record)))
        .map((record) => String(record.sessionId)));
      for (const [sessionId, unsubscribe] of liveDetailUnsubscribers.entries()) {
        if (wantedIds.has(sessionId)) continue;
        try { unsubscribe?.(); } catch (_) {}
        liveDetailUnsubscribers.delete(sessionId);
        liveDetailSignatures.delete(sessionId);
      }

      wantedIds.forEach((sessionId) => {
        if (liveDetailUnsubscribers.has(sessionId)) return;
        const unsubscribe = mcSubscribeCloudItem(detailStorageKeyFor(sessionId), (raw) => {
          applyLiveLedgerSnapshot(sessionId, raw);
        });
        liveDetailUnsubscribers.set(sessionId, unsubscribe);
      });

    };

    const readNow = (sourceRecords, { authoritative = false } = {}) => {
      // Ignore late callbacks from the organisation that was active when this effect
      // started. This closes the race where the old org's local/cloud event arrived
      // after login had already moved to the next org.
      const activeNow = String(getMcActiveOrgId() || "").trim();
      if (!mounted || !effectOrgId || activeNow !== effectOrgId) return;

      const hydrated = hydrateRecords(sourceRecords);
      const next = authoritative
        ? hydrated
        : mergeStableSessionRecords(stableRecordsRef.current, hydrated, archivedSessionIdsRef.current);
      const nextSignature = sessionRecordsSignature(next);

      if (recordsSignatureRef.current !== nextSignature) {
        recordsSignatureRef.current = nextSignature;
        stableRecordsRef.current = next;
        setRecords(next);
      }

      hydratedRef.current = true;
      setSessionsHydrated(true);
      setSessionsSyncing(false);
      syncLiveDetailSubscriptions(next);
    };


    const hydrateAuthoritativeDetails = async (sourceRecords, { authoritative = false } = {}) => {
      const rows = Array.isArray(sourceRecords) ? sourceRecords : [];
      const candidates = rows.filter((record) => record?.sessionId && (!isSessionSectionComplete(record) || !isSessionVerified(record)));
      if (!candidates.length) return;

      let hydratedAny = false;
      await Promise.all(candidates.map(async (record) => {
        try {
          let raw = await mcGetCloudItem(detailStorageKeyFor(record.sessionId));
          if (!raw) {
            raw = await mcGetCloudItem(legacyDetailStorageKeyFor(record.sessionId));
            if (raw) {
              // One-way migration: old live detail becomes the canonical ledger.
              mcSetCloudItemImmediate(detailStorageKeyFor(record.sessionId), raw).catch(() => {});
            }
          }
          if (!raw) return;
          let parsed = JSON.parse(raw);
          if (!parsed || !Array.isArray(parsed?.lines) || !parsed.lines.length) return;

          const touched = parsed.lines.filter((line) => line && (
            String(line.counted ?? "").trim() !== "" ||
            String(line.waste ?? "").trim() !== "" ||
            String(line.note ?? line.notes ?? "").trim() !== ""
          )).length;

          // If the canonical ledger has fallen back to a prepared 0/N shell, ask
          // the durability layer to recover only from a strictly richer existing
          // mirror. This never invents rows from today's inventory.
          if (touched === 0) {
            const repair = await ensureCanonicalCountLedger(record.sessionId, record, { allowDeepDiscovery: false });
            if (repair?.ok && repair?.value) {
              raw = repair.value;
              try { parsed = JSON.parse(raw); } catch (_) {}
            }
          }

          mcSetCachedItem(detailStorageKeyFor(record.sessionId), raw);
          hydratedAny = true;
        } catch (_) {}
      }));

      if (mounted && hydratedAny && String(getMcActiveOrgId() || '').trim() === effectOrgId) {
        // Re-resolve the same live ledger after its full detail snapshots are in the
        // browser cache. This immediately replaces stale 0/N summaries with the
        // real Firestore-backed count state without waiting for another list write.
        readNow(rows, { authoritative });
      }
    };

    // MC493 — bounded Sessions bootstrap. The compact routing documents tell us
    // which sessions exist; normal page hydration must never scan every document
    // in /morpheus just to rediscover IDs we already have. Exact Count Ledger
    // reads/listeners below hydrate only those known sessions. If both routing
    // indexes are missing, the administrator can still use explicit Recover Session,
    // whose deep compatibility scan remains intentionally available.
    Promise.all([
      mcGetCloudItem("mc-session-list").catch(() => null),
      mcGetCloudItem("mc-session-archive").catch(() => null),
      // Legacy routing backup remains read-only recovery evidence.
      mcGetCloudItem("mc-session-list-backup").catch(() => null),
    ]).then(([raw, archiveRaw, backupRaw]) => {
      const terminalCloudTimes = new Map();
      const terminalOperationalKeys = new Map();

      const registerArchiveRecord = (row = {}) => {
        const id = String(row?.sessionId || row?.id || "").trim();
        const terminalAt =
          Date.parse(row?.archivedAt || row?.updatedAt || row?.finalizedAt || row?.createdAt || "") || 0;

        if (id) {
          const currentIdTime = Number(terminalCloudTimes.get(id) || 0);
          terminalCloudTimes.set(id, Math.max(currentIdTime, terminalAt));
        }

        const opKey = durableOperationalKey(row);
        if (opKey) {
          const current = Number(terminalOperationalKeys.get(opKey) || 0);
          terminalOperationalKeys.set(opKey, Math.max(current, terminalAt));
        }
      };

      try {
        const parsedArchive = archiveRaw ? JSON.parse(archiveRaw) : [];
        (Array.isArray(parsedArchive) ? parsedArchive : []).forEach(registerArchiveRecord);
      } catch (_) {}

      let cloudRecords = [];
      try {
        const parsed = raw ? JSON.parse(raw) : [];
        cloudRecords = Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        cloudRecords = [];
      }

      let backupRecords = [];
      try {
        const parsedBackup = backupRaw ? JSON.parse(backupRaw) : [];
        backupRecords = (Array.isArray(parsedBackup) ? parsedBackup : []).filter((record) => {
          const id = String(record?.sessionId || record?.id || "").trim();
          const orgId = String(record?.tenantOrgId || record?.orgId || "").trim();
          const status = String(record?.status || "").trim().toLowerCase();
          if (!id || orgId !== effectOrgId) return false;
          return !(record?.archived === true || record?.archivedAt || record?.deletedAt ||
            ["archived", "archive", "deleted"].includes(status));
        });
      } catch (_) {
        backupRecords = [];
      }

      if (!mounted || String(getMcActiveOrgId() || "").trim() !== effectOrgId) return;

      // Browser-cached ledgers may still supplement metadata for IDs already known
      // to this tenant, but they are never a reason to perform a collection scan.
      const discoveredRecords = loadSessionRecords();
      const bootstrapById = new Map();
      [...cloudRecords, ...discoveredRecords, ...backupRecords].forEach((record) => {
        const id = String(record?.sessionId || "").trim();
        if (!id) return;

        const liveTime = durableRecordTime(record);
        const exactTerminalAt = Number(terminalCloudTimes.get(id) || 0);
        if (exactTerminalAt && exactTerminalAt >= liveTime) return;

        const opKey = durableOperationalKey(record);
        const operationalTerminalAt = opKey
          ? Number(terminalOperationalKeys.get(opKey) || 0)
          : 0;
        if (operationalTerminalAt && operationalTerminalAt >= liveTime) return;

        const existing = bootstrapById.get(id);
        bootstrapById.set(id, existing
          ? { ...existing, ...record, meta: { ...(existing?.meta || {}), ...(record?.meta || {}) } }
          : record);
      });

      const bootstrapRecords = Array.from(bootstrapById.values());
      canonicalDiscoveryReady = true;
      readNow(bootstrapRecords, { authoritative: false });
      hydrateAuthoritativeDetails(bootstrapRecords, { authoritative: false });

      if (deferredEmptyLiveRecords) {
        deferredEmptyLiveRecords = null;
        const durableRecords = loadSessionRecords();
        readNow(durableRecords, { authoritative: false });
        hydrateAuthoritativeDetails(durableRecords, { authoritative: false });
      }
    }).catch(() => {
      canonicalDiscoveryReady = true;
      readNow(loadSessionRecords(), { authoritative: false });
    });

    const unsubscribeLiveSessions = subscribeSessionListLive((liveRecords) => {
      const rows = Array.isArray(liveRecords) ? liveRecords : [];
      if (!canonicalDiscoveryReady && rows.length === 0) {
        deferredEmptyLiveRecords = rows;
        return;
      }
      // A non-empty routing list can render immediately. Once canonical discovery is
      // ready, merge its cached ledger safety-net before accepting an empty list.
      if (canonicalDiscoveryReady && rows.length === 0) {
        const durableRecords = loadSessionRecords();
        readNow(durableRecords, { authoritative: false });
        hydrateAuthoritativeDetails(durableRecords, { authoritative: false });
        return;
      }

      // A non-empty session-list snapshot is not proof that the list is complete.
      // Merge it with ledger discovery on every callback. This preserves sibling
      // sessions from the same operational date while still allowing archived /
      // finalized ledgers to be removed by loadSessionRecords().
      const durableRecords = loadSessionRecords();
      const liveById = new Map();
      [...rows, ...durableRecords].forEach((record) => {
        const id = String(record?.sessionId || '').trim();
        if (!id) return;
        const existing = liveById.get(id);
        liveById.set(id, existing
          ? { ...existing, ...record, meta: { ...(existing?.meta || {}), ...(record?.meta || {}) } }
          : record);
      });
      const mergedRows = Array.from(liveById.values());
      readNow(mergedRows, { authoritative: false });
      hydrateAuthoritativeDetails(mergedRows, { authoritative: false });
    });

    // subscribeSessionListLive is the single data authority for this page. It
    // already invokes the callback after applying the archive filter. Do not also
    // consume MC_SESSION_EVENT here: that event is emitted by the same listener
    // for legacy consumers and caused Sessions to process every snapshot twice.
    const stopForRoute = () => {
      mounted = false;
      if (hydrateTimer) window.clearTimeout(hydrateTimer);
      unsubscribeLiveSessions?.();
      for (const unsubscribe of liveDetailUnsubscribers.values()) {
        try { unsubscribe?.(); } catch (_) {}
      }
      liveDetailUnsubscribers.clear();
      liveDetailSignatures.clear();
    };

    window.addEventListener("mc-tab-navigate-start", stopForRoute);
    hydrateTimer = window.setTimeout(() => {
      if (!mounted || hydratedRef.current) return;

      // Never leave the screen in a permanent loading state if Firestore is slow,
      // offline, or the organisation has no sessions document yet. The listener
      // remains active and will replace this authoritative empty state as soon as
      // the org-scoped snapshot arrives.
      readNow([], { authoritative: true });
    }, 8000);
    return () => {
      mounted = false;
      if (hydrateTimer) window.clearTimeout(hydrateTimer);
      unsubscribeLiveSessions?.();
      window.removeEventListener("mc-tab-navigate-start", stopForRoute);
    };
  }, [sessionHydrationScopeKey]);



  useEffect(() => {
    const removeRefreshReset = installOperationalDateRefreshReset();
    return () => {
      removeRefreshReset();
      if (openingTimerRef.current) window.clearTimeout(openingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    rememberOperationalDate(selectedDate);
  }, [selectedDate]);

  const warmSessionDetail = (session) => {
    const sessionId = String(session?.sessionId || "").trim();
    if (!sessionId) return Promise.resolve(null);

    const cacheKey = detailStorageKeyFor(sessionId);
    const cached = mcGetItem(cacheKey);
    if (cached) return Promise.resolve(cached);

    const existing = detailWarmupRef.current.get(sessionId);
    if (existing) return existing;

    const request = mcGetCloudItem(cacheKey)
      .then(async (payload) => {
        let resolved = payload;
        if (!resolved) {
          resolved = await mcGetCloudItem(legacyDetailStorageKeyFor(sessionId));
          if (resolved) mcSetCloudItemImmediate(cacheKey, resolved).catch(() => {});
        }
        if (resolved) {
          try { mcSetItem(cacheKey, resolved); } catch (_) {}
        }
        return resolved;
      })
      .catch((error) => {
        console.warn("Live count warm-up failed", { sessionId, error });
        return null;
      })
      .finally(() => {
        detailWarmupRef.current.delete(sessionId);
      });

    detailWarmupRef.current.set(sessionId, request);
    return request;
  };

  const beginOpenSession = (session) => {
    if (openingTimerRef.current) window.clearTimeout(openingTimerRef.current);
    warmSessionDetail(session);
    setOpeningSession({
      sessionId: session?.sessionId || "",
      title: buildSessionDisplayName(session),
      startedAt: Date.now(),
    });
    // Only call the opening genuinely slow after ten seconds. Most counts should
    // already be warm in the local cache from hover/focus before navigation.
    openingTimerRef.current = window.setTimeout(() => {
      setOpeningSession((current) => current ? { ...current, slow: true } : current);
    }, 10000);
  };

  const sessions = useMemo(() => {
    // Active sessions are controlled by the active session list only. Do not let a
    // stale archive-list snapshot hide a recovered/restored session a few seconds later.
    const safeRecords = records.filter((record) => {
      if (!record?.sessionId || !recordBelongsToCurrentOrgSites(record)) return false;
      if (record.archived === true || record.archivedAt || record.deletedAt) return false;
      const status = String(record.status || "").trim().toLowerCase();
      return status !== "archived" && status !== "archive";
    });
    return filterRecordsForProfile(safeRecords, currentProfile);
  }, [adminStoreView, currentProfile, records]);


  const liveSessions = sessions.filter((item) => String(item.status || "").toLowerCase() !== "completed");
  const completedSessions = sessions.filter((item) => String(item.status || "").toLowerCase() === "completed");
  const availableDates = useMemo(() => Array.from(new Set(sessions.map(sessionDateKey)))
    .filter((date) => date !== 'Undated')
    .sort((a, b) => String(b).localeCompare(String(a))), [sessions]);
  useEffect(() => {
    if (reviewDateAppliedRef.current !== reviewNavigationKey) {
      const target = sessions.find((item) => item.sessionId === sessionReviewTarget.sessionId);
      const requestedDate = target ? sessionDateKey(target) : sessionReviewTarget.date;
      if (requestedDate && availableDates.includes(requestedDate)) {
        // Keep a partial date match pending until its requested count arrives.
        // Once applied, later user date changes remain in control.
        if (!sessionReviewTarget.sessionId || target) reviewDateAppliedRef.current = reviewNavigationKey;
        setSelectedDate(requestedDate);
        if (target) setCollapsedSites((current) => {
          const site = resolveSessionSiteName(target);
          // This existing set stores expanded sites despite its legacy name.
          if (current.has(site)) return current;
          const next = new Set(current); next.add(site); return next;
        });
        return;
      }
      // Do not replace a requested older day with the latest day while the
      // existing Sessions hydration is still delivering its routing records.
      if ((sessionReviewTarget.date || sessionReviewTarget.sessionId) && (!sessionsHydrated || sessionsSyncing)) return;
      // A partial routing snapshot may arrive before the requested count. Keep
      // the link pending so the existing later hydration can select its day.
      if (!sessionReviewTarget.date && !sessionReviewTarget.sessionId) reviewDateAppliedRef.current = reviewNavigationKey;
    }
    if (availableDates.length && !availableDates.includes(selectedDate)) setSelectedDate(availableDates[0]);
  }, [availableDates, selectedDate, sessions, sessionReviewTarget, reviewNavigationKey, sessionsHydrated, sessionsSyncing]);
  const dateSessions = useMemo(() => sessions.filter((item) => sessionDateKey(item) === selectedDate), [sessions, selectedDate]);
  const totalFlags = dateSessions.reduce((sum, item) => sum + Number(item.flaggedCount || 0), 0);
  const groupedSessions = useMemo(() => buildGroupedSessions(dateSessions), [dateSessions]);
  useEffect(() => {
    if (!sessionReviewTarget.sessionId || reviewScrolledRef.current === reviewNavigationKey) return;
    const target = dateSessions.find((item) => item.sessionId === sessionReviewTarget.sessionId);
    if (!target) return;
    const element = document.getElementById(`mc-session-row-${encodeURIComponent(target.sessionId)}`);
    if (!element) return;
    reviewScrolledRef.current = reviewNavigationKey;
    element.scrollIntoView({ behavior: 'auto', block: 'center' });
    element.focus({ preventScroll: true });
  }, [dateSessions, collapsedSites, sessionReviewTarget.sessionId, reviewNavigationKey]);
  const totalExposure = groupedSessions.reduce((sum, siteGroup) => sum + latestCycleExposureForSite(siteGroup), 0);
  const activeCycleCount = groupedSessions.reduce((sum, site) => sum + site.cycles.length, 0);
  const completedSectionCount = dateSessions.filter((item) => isSessionSectionComplete(item)).length;

  const toggleSiteCollapsed = (siteName) => {
    setCollapsedSites((current) => {
      const next = new Set(current);
      if (next.has(siteName)) next.delete(siteName);
      else next.add(siteName);
      return next;
    });
  };

  // Compare intelligence is independent from lifecycle location.
  // Sessions is the operational workspace; Archives is historical truth. Finalizing
  // a count must remove it from operational Sessions without removing it from
  // comparison intelligence. Read both projections on demand and never restore or
  // mutate an archive record just to compare it.
  const allCompareSessions = useMemo(() => {
    const byId = new Map();

    const add = (item, source) => {
      const id = String(item?.sessionId || '').trim();
      if (!id) return;
      const existing = byId.get(id);

      // Prefer the active Sessions projection when the same session legitimately
      // exists in both caches during a lifecycle transition; otherwise retain the
      // archived record as historical comparison authority.
      if (!existing || source === "active") {
        byId.set(id, { ...item, __compareSource: source });
      }
    };

    // Historical comparison authority is FINALIZED archive state only.
    (loadArchivedSessionRecords() || [])
      .filter(isFinalizedHistoricalCount)
      .forEach((item) => add(item, "archive"));

    // Keep operational Sessions in the set so the count currently being worked
    // can be A. Other unfinalized sessions are never offered as historical B.
    sessions.forEach((item) => add(item, "active"));

    return Array.from(byId.values());
  }, [sessions]);

  const compareSessionById = (sessionId) => allCompareSessions.find((item) => item?.sessionId === sessionId) || null;

  const compareIdentity = (session = {}) => ({
    section: normalizeSectionId(resolveSessionSectionName(session)),
    site: String(resolveSessionSiteName(session) || "").trim().toLowerCase(),
  });

  // CI3: comparison compatibility follows the Morpheus comparison contract:
  // same site + same section. Cycle, operational date and product/category scope
  // may differ; CI1/CI2 compare only the overlapping products and report exclusions.
  const compareCompatibility = (candidate, selected) => {
    if (!candidate || !selected) return { allowed: true, reason: "" };
    const a = compareIdentity(candidate);
    const b = compareIdentity(selected);
    if (a.site !== b.site) return { allowed: false, reason: "Compare sessions from the same site." };
    if (a.section !== b.section) return { allowed: false, reason: `Select another ${resolveSessionSectionName(selected)} session.` };
    if (candidate?.sessionId && candidate.sessionId === selected?.sessionId) return { allowed: false, reason: "Select a different count session." };
    return { allowed: true, reason: "" };
  };

  const toggleCompareSession = (sessionId) => {
    setCompareSessionIds((current) => {
      const next = new Set(current);
      if (next.has(sessionId)) {
        next.delete(sessionId);
        setCompareStatus("");
        return next;
      }
      if (next.size >= 2) return next;
      const candidate = compareSessionById(sessionId);
      const firstId = Array.from(next)[0];
      const first = firstId ? compareSessionById(firstId) : null;
      const compatibility = compareCompatibility(candidate, first);
      if (!compatibility.allowed) {
        setCompareStatus(compatibility.reason);
        return next;
      }
      next.add(sessionId);
      setCompareStatus(next.size === 1 ? `Choose from the latest 30 comparable counts — completed Sessions and finalized Archives.` : "Ready to compare.");
      if (next.size === 1) {
        window.setTimeout(() => {
          compareTrayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          window.setTimeout(() => {
            const picker = compareHistorySelectRef.current;
            if (!picker) return;
            picker.focus();
            try {
              if (typeof picker.showPicker === "function") picker.showPicker();
            } catch (_) {}
          }, 220);
        }, 0);
      }
      return next;
    });
  };

  const firstCompareSession = compareSessionIds.size === 1
    ? compareSessionById(Array.from(compareSessionIds)[0])
    : null;

  const historicalCompareCandidates = useMemo(() => {
    if (!firstCompareSession) return [];
    const firstId = String(firstCompareSession?.sessionId || '');
    const getTime = (item = {}) => {
      const raw = item?.sectionCompletedAt || item?.completedAt || item?.archivedAt || item?.updatedAt || item?.startedAt || item?.createdAt || 0;
      const time = new Date(raw || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    // Sessions Compare shows one combined recent history:
    // - completed compatible counts still in Sessions;
    // - finalized compatible counts already in Archives.
    // Sort both sources together and expose only the latest 30.
    return allCompareSessions
      .filter((candidate) => String(candidate?.sessionId || '') !== firstId)
      .filter((candidate) => compareCompatibility(candidate, firstCompareSession).allowed)
      .filter((candidate) => (
        candidate?.__compareSource === "active"
          ? isSessionSectionComplete(candidate)
          : candidate?.__compareSource === "archive" && isFinalizedHistoricalCount(candidate)
      ))
      .sort((a, b) => getTime(b) - getTime(a))
      .slice(0, 30);
  }, [firstCompareSession, allCompareSessions]);

  const chooseHistoricalCompareSession = (sessionId) => {
    const cleanId = String(sessionId || '').trim();
    if (!cleanId || !firstCompareSession) return;
    const candidate = compareSessionById(cleanId);
    const compatibility = compareCompatibility(candidate, firstCompareSession);
    if (!compatibility.allowed) {
      setCompareStatus(compatibility.reason);
      return;
    }
    setCompareSessionIds(new Set([firstCompareSession.sessionId, cleanId]));
    setCompareStatus("Ready to compare.");
  };

  const compareSelectedSessions = () => {
    if (compareSessionIds.size !== 2) return;
    const selected = Array.from(compareSessionIds).map(compareSessionById).filter(Boolean);
    if (selected.length !== 2) return;
    const compatibility = compareCompatibility(selected[1], selected[0]);
    if (!compatibility.allowed) {
      setCompareStatus(compatibility.reason);
      return;
    }

    // MC467 — comparison identity is chronological, never click-order. Always send
    // the older count first so A is the baseline and B is the newer result. This
    // keeps Sessions, Archives and the automatic completion prompt consistent.
    const compareTime = (item = {}) => {
      const raw = item?.sectionCompletedAt || item?.completedAt || item?.archivedAt || item?.updatedAt || item?.startedAt || item?.createdAt || 0;
      const time = new Date(raw || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };
    const chronological = [...selected].sort((a, b) => compareTime(a) - compareTime(b));
    navigate(`/morpheus-count/archive?compare=1&ids=${encodeURIComponent(chronological.map((item) => item.sessionId).join(','))}&source=active`);
  };

  // CI3: keep the first comparison selection when the manager changes the
  // operational date. This is what makes cross-day comparison possible from
  // Sessions without loading every day into the page at once.

  const verifySession = async (session) => {
    if (!isAdmin || !session?.sessionId || !isSessionSectionComplete(session) || verifyBusyId) return;
    const now = new Date().toISOString();
    const verifiedBy = buildProfileDisplayName(currentProfile) || currentProfile?.email || 'Administrator';
    setVerifyBusyId(session.sessionId);
    try {
      // Phase 3: verification belongs to the canonical completed Count Ledger.
      // Sessions is only a derived list, so verify the ledger first and mirror the
      // result into the list only after Firestore acknowledges the transition.
      const ledgerKey = `mc-count-ledger:${session.sessionId}`;
      let ledgerRaw = await mcGetCloudItem(ledgerKey);
      if (!ledgerRaw) ledgerRaw = mcGetItem(ledgerKey) || "";
      let parsedLedger = null;
      try { parsedLedger = ledgerRaw ? JSON.parse(ledgerRaw) : null; } catch (_) {}
      if (!parsedLedger || !Array.isArray(parsedLedger.lines) || !parsedLedger.lines.length) {
        const repair = await ensureCanonicalCountLedger(session.sessionId, session);
        if (!repair?.ok || !repair?.value) {
          throw new Error("Canonical Count Ledger is unavailable for verification and no durable row snapshot could be recovered.");
        }
        ledgerRaw = repair.value;
      }
      const verification = await mcTransitionCountLedger(ledgerKey, ledgerRaw, "verify", {
        name: verifiedBy,
        profileId: currentProfile?.id || "",
      });
      if (!verification?.ok) throw new Error(`Canonical verification failed: ${verification?.reason || "unknown"}`);

      // P4.1: acknowledgement updates the canonical ledger cache, then Sessions
      // re-projects that ledger locally. Do not persist a second verification truth
      // into mc-session-list. Other browsers receive the same ledger via observer.
      if (verification?.value) mcSetCachedItem(ledgerKey, verification.value);
      const nextRecords = stableRecordsRef.current.map((record) => {
        if (record?.sessionId !== session.sessionId) return record;
        return normalizeActiveSessionRecord(resolveSessionRecord(record, currentProfile)) || record;
      });
      stableRecordsRef.current = nextRecords;
      recordsSignatureRef.current = sessionRecordsSignature(nextRecords);
      setRecords(nextRecords);
    } finally {
      setVerifyBusyId('');
      setConfirmVerify(null);
    }
  };

  const archiveRecord = async (sessionId) => {
    if (archiveBusyId) return;
    setArchiveBusyId(sessionId);
    try {
      archivedSessionIdsRef.current.add(sessionId);
      const next = await archiveSessionRecord(sessionId, "Archived from sessions list", null, confirmArchive);
      const refreshed = next.active.map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed;
      recordsSignatureRef.current = sessionRecordsSignature(refreshed);
      setRecords(refreshed);
      setConfirmArchive(null);
    } finally {
      setArchiveBusyId("");
    }
  };

  const finalizeRecord = async (sessionId) => {
    if (finalizeBusyId) return;
    setFinalizeBusyId(sessionId);
    try {
      archivedSessionIdsRef.current.add(sessionId);
      await clearLiveCountSaveQueueForTerminalSession(sessionId, { drainMs: 5000 });
      const next = await archiveSessionRecord(sessionId, "Finalized completed count", null, confirmFinalize?.session || confirmFinalize);
      const refreshed = next.active.map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed;
      recordsSignatureRef.current = sessionRecordsSignature(refreshed);
      setRecords(refreshed);
      setConfirmFinalize(null);
    } finally {
      setFinalizeBusyId("");
    }
  };

  const finalizeCycle = (cycle) => {
    const cycleSessions = Array.isArray(cycle?.sessions) ? cycle.sessions : [];
    const completed = cycleSessions.filter((item) => isSessionSectionComplete(item));
    const active = cycleSessions.filter((item) => !isSessionSectionComplete(item));
    const unverified = completed.filter((item) => !isSessionVerified(item));

    if (!completed.length) {
      setConfirmFinalize({ type: "blocked", title: cycle?.cycleName || "Cycle", message: "At least one section must be completed before this cycle can be finalized." });
      return;
    }

    if (active.length) {
      setConfirmFinalize({
        type: "blocked",
        title: cycle?.cycleName || "Cycle",
        message: `Complete or archive all active sections first: ${active.map((item) => resolveSessionSectionName(item)).join(", ")}.`
      });
      return;
    }

    if (unverified.length) {
      setConfirmFinalize({
        type: "blocked",
        title: cycle?.cycleName || "Cycle",
        message: `Verify every completed section before finalizing. Still unverified: ${unverified.map((item) => resolveSessionSectionName(item)).join(", ")}.`
      });
      return;
    }

    setConfirmFinalize({
      type: "cycle",
      title: cycle?.cycleName || "Cycle",
      cycle: { ...cycle, sessions: completed },
      message: `Finalize ${cycle?.cycleName || "this cycle"}? This will move ${completed.length} completed section${completed.length === 1 ? "" : "s"} to Archives.`
    });
  };

  const executeFinalizeCycle = async (cycle) => {
    const completed = (Array.isArray(cycle?.sessions) ? cycle.sessions : []).filter((item) => isSessionSectionComplete(item));
    if (!completed.length || finalizeBusyId) return;

    setFinalizeBusyId(`cycle:${cycle?.cycleId || cycle?.cycleName || "cycle"}`);
    try {
      let latestActive = null;
      for (const session of completed) {
        archivedSessionIdsRef.current.add(session.sessionId);
        const next = await archiveSessionRecord(session.sessionId, `Finalized cycle: ${cycle?.cycleName || "Cycle"}`, null, session);
        latestActive = next?.active || latestActive;
      }
      const refreshed = (latestActive || loadSessionRecords()).map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed;
      recordsSignatureRef.current = sessionRecordsSignature(refreshed);
      setRecords(refreshed);
      setConfirmFinalize(null);
    } finally {
      setFinalizeBusyId("");
    }
  };


  const finalizeAllCycles = (siteGroup) => {
    const cycles = Array.isArray(siteGroup?.cycles) ? siteGroup.cycles : [];
    const allSessions = cycles.flatMap((cycle) => Array.isArray(cycle?.sessions) ? cycle.sessions : []);
    const completed = allSessions.filter((item) => isSessionSectionComplete(item));
    const active = allSessions.filter((item) => !isSessionSectionComplete(item));
    const unverified = completed.filter((item) => !isSessionVerified(item));

    if (!completed.length) {
      setConfirmFinalize({
        type: "blocked",
        title: siteGroup?.siteName || "Site",
        message: "At least one completed cycle is required before all cycles can be finalized."
      });
      return;
    }

    if (active.length) {
      const activeCycles = cycles
        .filter((cycle) => (cycle?.sessions || []).some((item) => !isSessionSectionComplete(item)))
        .map((cycle) => cycle?.cycleName || "Cycle");
      setConfirmFinalize({
        type: "blocked",
        title: siteGroup?.siteName || "Site",
        message: `Complete or archive all active sections first. Cycles still in progress: ${activeCycles.join(", ")}.`
      });
      return;
    }

    if (unverified.length) {
      setConfirmFinalize({
        type: "blocked",
        title: siteGroup?.siteName || "Site",
        message: `${unverified.length} completed section${unverified.length === 1 ? " is" : "s are"} still unverified. Verify all sections before finalizing the site.`
      });
      return;
    }

    setConfirmFinalize({
      type: "site",
      title: siteGroup?.siteName || "Site",
      siteGroup: { ...siteGroup, cycles },
      message: `Finalize all ${cycles.length} cycle${cycles.length === 1 ? "" : "s"} for ${siteGroup?.siteName || "this site"}? This will move ${completed.length} completed section${completed.length === 1 ? "" : "s"} to Archives.`
    });
  };

  const executeFinalizeAllCycles = async (siteGroup) => {
    const cycles = Array.isArray(siteGroup?.cycles) ? siteGroup.cycles : [];
    const completed = cycles
      .flatMap((cycle) => (Array.isArray(cycle?.sessions) ? cycle.sessions : []).map((session) => ({ cycle, session })))
      .filter(({ session }) => isSessionSectionComplete(session));
    if (!completed.length || finalizeBusyId) return;

    setFinalizeBusyId(`site:${siteGroup?.siteName || "site"}`);
    try {
      let latestActive = null;
      for (const { cycle, session } of completed) {
        // MC427 — finalization owns the terminal boundary. Drain/retire any stale
        // live-count autosave for this already completed + verified session before
        // taking the canonical finalize lock. This prevents Firestore transaction
        // contention from spiralling into failed-precondition/resource-exhausted.
        await clearLiveCountSaveQueueForTerminalSession(session.sessionId, { drainMs: 5000 });
        archivedSessionIdsRef.current.add(session.sessionId);
        const next = await archiveSessionRecord(
          session.sessionId,
          `Finalized all cycles for ${siteGroup?.siteName || "site"}: ${cycle?.cycleName || "Cycle"}`,
          null,
          session
        );
        latestActive = next?.active || latestActive;
        // Avoid burst-writing several cycle/archive projections in the same tick.
        await new Promise((resolve) => setTimeout(resolve, 180));
      }
      const refreshed = (latestActive || loadSessionRecords()).map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed;
      recordsSignatureRef.current = sessionRecordsSignature(refreshed);
      setRecords(refreshed);
      setConfirmFinalize(null);
    } finally {
      setFinalizeBusyId("");
    }
  };

  const requestBulkArchive = (type, label, targetSessions) => {
    const items = (Array.isArray(targetSessions) ? targetSessions : []).filter((item) => item?.sessionId);
    if (!items.length) return;
    setBulkArchiveTarget({ type, label, sessions: items });
  };

  const executeBulkArchive = async () => {
    const target = bulkArchiveTarget;
    if (!target?.sessions?.length || archiveBusyId) return;
    setArchiveBusyId(`bulk:${target.type}`);
    try {
      let latestActive = null;
      for (const session of target.sessions) {
        archivedSessionIdsRef.current.add(session.sessionId);
        const next = await archiveSessionRecord(session.sessionId, `Bulk archived ${target.type}: ${target.label}`, null, session);
        latestActive = next?.active || latestActive;
      }
      const refreshed = (latestActive || loadSessionRecords()).map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed;
      recordsSignatureRef.current = sessionRecordsSignature(refreshed);
      setRecords(refreshed);
      setBulkArchiveTarget(null);
    } finally { setArchiveBusyId(''); }
  };

  const finalizeDay = () => {
    const completed = dateSessions.filter(isSessionSectionComplete);
    const active = dateSessions.filter((item) => !isSessionSectionComplete(item));
    const unverified = completed.filter((item) => !isSessionVerified(item));
    if (!completed.length || active.length) {
      setConfirmFinalize({ type: 'blocked', title: formatOperationalDate(selectedDate), message: active.length ? `${active.length} section${active.length === 1 ? '' : 's'} still in progress. Complete or archive them before finalizing the day.` : 'No completed sections are ready to finalize.' });
      return;
    }
    if (unverified.length) {
      setConfirmFinalize({ type: 'blocked', title: formatOperationalDate(selectedDate), message: `${unverified.length} completed section${unverified.length === 1 ? ' is' : 's are'} still unverified. Verify every section before finalizing the day.` });
      return;
    }
    setConfirmFinalize({ type: 'day', title: formatOperationalDate(selectedDate), daySessions: completed, message: `Finalize all ${completed.length} completed sections for this operational day?` });
  };

  const executeFinalizeDay = async () => {
    const completed = confirmFinalize?.daySessions || [];
    if (!completed.length || finalizeBusyId) return;
    setFinalizeBusyId(`day:${selectedDate}`);
    try {
      let latestActive = null;
      for (const session of completed) {
        // MC433 — day finalization must not depend on the projection captured when
        // the confirmation modal opened. Re-resolve the latest durable Sessions
        // record and ensure its canonical ledger before crossing the terminal
        // boundary. This makes Finalize Day use the same hardened lifecycle path as
        // the working single-cycle buttons.
        const currentSession = loadSessionRecords().find((item) => item?.sessionId === session?.sessionId) || session;
        const ensured = await ensureCanonicalCountLedger(currentSession.sessionId, currentSession);
        if (!ensured?.ok) {
          throw new Error(`Cannot finalize day: ${currentSession.sessionId} has no canonical count ledger (${ensured?.reason || 'unknown'}).`);
        }
        archivedSessionIdsRef.current.add(currentSession.sessionId);
        const next = await archiveSessionRecord(currentSession.sessionId, `Finalized operational day: ${selectedDate}`, null, currentSession);
        latestActive = next?.active || latestActive;
      }
      const refreshed = (latestActive || loadSessionRecords()).map((record) => resolveSessionRecord(record, currentProfile));
      stableRecordsRef.current = refreshed; recordsSignatureRef.current = sessionRecordsSignature(refreshed); setRecords(refreshed); setConfirmFinalize(null);
    } finally { setFinalizeBusyId(''); }
  };

  const recoverMissingSession = async () => {
    const cleanId = recoverSessionId.trim();
    if (!cleanId || recoverBusy) return;
    setRecoverBusy(true);
    setRecoverStatus("Checking Firestore for session detail...");
    try {
      const result = await recoverMissingSessionRecord(cleanId);
      if (result?.ok) {
        const recoveredRecords = loadSessionRecords().map((record) => normalizeActiveSessionRecord(resolveSessionRecord(record, currentProfile))).filter(Boolean);
        stableRecordsRef.current = recoveredRecords;
        recordsSignatureRef.current = sessionRecordsSignature(recoveredRecords);
        setRecords(recoveredRecords);
        setRecoverStatus(result.reason || "Session recovered.");
        setRecoverSessionId("");
      } else {
        setRecoverStatus(result?.reason || "Could not recover that session.");
      }
    } catch (err) {
      setRecoverStatus(`Recovery failed: ${err?.message || err}`);
    } finally {
      setRecoverBusy(false);
    }
  };


  return (
    <div className="mc-sessions-page mc-tabPage">
      <div className="mc-sessions-bg" />

      <McPageHeader activeKey="sessions" />


      <section className="mc-operational-day-command">
        <div className="mc-operational-day-main">
          <div>
            <div className="mc-operational-day-label-row">
              <span className="mc-sessions-label">Operational date</span>
              <span className="mc-daily-command-version">Daily Command v4</span>
            </div>
            <h2>{formatOperationalDate(selectedDate)}</h2>
            <p>{dateSessions.length} section session{dateSessions.length === 1 ? '' : 's'} across {groupedSessions.length} site{groupedSessions.length === 1 ? '' : 's'}.</p>
          </div>
          <div className="mc-operational-day-controls">
            <select value={selectedDate} onChange={(event) => {
              reviewDateAppliedRef.current = reviewNavigationKey;
              reviewScrolledRef.current = reviewNavigationKey;
              setSelectedDate(event.target.value);
            }} aria-label="Operational date">
              {availableDates.map((date) => <option key={date} value={date}>{formatOperationalDate(date)}</option>)}
            </select>
            {canFinalize && dateSessions.length ? <button className="mc-row-btn mc-row-btn--primary" type="button" onClick={finalizeDay} disabled={Boolean(finalizeBusyId)}>Finalize Day</button> : null}
            {isAdmin && dateSessions.length ? <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => requestBulkArchive('day', formatOperationalDate(selectedDate), dateSessions)} disabled={Boolean(archiveBusyId)}>Archive Day</button> : null}
          </div>
        </div>
      </section>

      {compareSessionIds.size > 0 ? (
        <section ref={compareTrayRef} className="mc-compare-selection-tray" aria-live="polite">
          <div>
            <span className="mc-sessions-label">Comparison selection</span>
            <strong>{compareSessionIds.size === 1 ? "1 count selected — choose its matching section" : "2 matching counts selected"}</strong>
            <p>{compareStatus || "Same site + same section. Cycle, date and product scope may differ."}</p>
          </div>
          <div className="mc-compare-selection-items">
            {Array.from(compareSessionIds).map((id, index) => {
              const item = compareSessionById(id);
              if (!item) return null;
              return (
                <div className="mc-compare-selection-item" key={id}>
                  <span>{index === 0 ? "A" : "B"}</span>
                  <div>
                    <strong>{resolveSessionSectionName(item)}</strong>
                    <small>{resolveSessionSiteName(item)} · {formatOperationalDate(sessionDateKey(item))} · {resolveSessionCycle(item)?.cycleName || item?.cycleName || "Cycle"}</small>
                  </div>
                </div>
              );
            })}
            {compareSessionIds.size === 1 ? (
              <div className="mc-compare-history-picker">
                <label htmlFor="mc-compare-history-select">Compare with another date</label>
                <select
                  ref={compareHistorySelectRef}
                  id="mc-compare-history-select"
                  defaultValue=""
                  onChange={(event) => chooseHistoricalCompareSession(event.target.value)}
                  disabled={!historicalCompareCandidates.length}
                >
                  <option value="">{historicalCompareCandidates.length ? "Choose previous comparable count…" : "No completed Session or finalized Archive count found"}</option>
                  {historicalCompareCandidates.map((candidate) => (
                    <option key={candidate.sessionId} value={candidate.sessionId}>
                      {formatOperationalDate(sessionDateKey(candidate))} · {resolveSessionCycle(candidate)?.cycleName || candidate?.cycleName || "Cycle"} · {Number(candidate?.totalLines || candidate?.detailSnapshot?.lines?.length || 0)} products
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => { setCompareSessionIds(new Set()); setCompareStatus(""); }}>Clear</button>
            <button className="mc-row-btn mc-row-btn--primary" type="button" onClick={compareSelectedSessions} disabled={compareSessionIds.size !== 2}>Compare Counts</button>
          </div>
        </section>
      ) : null}

      {isAdmin ? (
        <div className="mc-session-recovery-card">
          <div>
            <span className="mc-session-recovery-eyebrow">Manual recovery</span>
            <strong>Recover a missing session from Firestore</strong>
            <p>Paste the exact session ID — legacy or new collision-safe format. This restores only that session into Sessions.</p>
          </div>
          <div className="mc-session-recovery-actions">
            <input
              value={recoverSessionId}
              onChange={(event) => setRecoverSessionId(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') recoverMissingSession(); }}
              placeholder="MC-260816-211959-277A8F16978B"
              aria-label="Session ID to recover"
            />
            <button type="button" className="mc-row-btn mc-row-btn--primary" onClick={recoverMissingSession} disabled={recoverBusy || !recoverSessionId.trim()}>
              {recoverBusy ? 'Recovering...' : 'Recover Session'}
            </button>
          </div>
          {recoverStatus ? <p className="mc-session-recovery-status">{recoverStatus}</p> : null}
        </div>
      ) : null}

      <div className="mc-sessions-topbar">
        <div className="mc-topbar-card"><span>Active Cycles</span><strong>{activeCycleCount}</strong></div>
        <div className="mc-topbar-card"><span>Sections Completed</span><strong>{completedSectionCount}</strong></div>
        <div className="mc-topbar-card"><span>Awaiting Verification</span><strong>{dateSessions.filter((item) => isSessionSectionComplete(item) && !isSessionVerified(item)).length}</strong></div>
        <div className="mc-topbar-card"><span>Current Flags</span><strong>{totalFlags}</strong></div>
        <div className="mc-topbar-card mc-topbar-card--exposure"><span>Exposure</span><strong className={totalExposure > 0 ? 'is-positive' : totalExposure < 0 ? 'is-negative' : ''}>{formatExposure(totalExposure)}</strong></div>
      </div>

      {sessionsSyncing ? (
        <div className="mc-dataLoadingIndicator" role="status" aria-live="polite" style={{ position: "relative", top: "auto", right: "auto", width: "fit-content", margin: "0 0 14px auto" }}>
          <span className="mc-dataLoadingIndicator__spinner" aria-hidden="true" />
          <span>Loading data…</span>
        </div>
      ) : null}

      <div className="mc-sessions-list mc-sessions-cycleList">
        {!sessions.length ? (
          <div className="mc-session-empty">
            <strong>{(!sessionsHydrated || sessionsSyncing) ? "Loading live sessions…" : "No count session prepared yet."}</strong>
            <p>{(!sessionsHydrated || sessionsSyncing) ? "MC is syncing the latest live sessions and durable recovery ledger from Firestore." : "Import a CSV, review the data, then prepare the count session to see it here."}</p>
            {(sessionsHydrated && !sessionsSyncing) ? <Link className="mc-primary-btn mc-link-btn" to="/morpheus-count/imports">Go to Import</Link> : null}
          </div>
        ) : groupedSessions.map((siteGroup) => {
          const siteCollapsed = !collapsedSites.has(siteGroup.siteName);
          const siteSessions = siteGroup.cycles.flatMap((cycle) => cycle.sessions || []);
          const siteCompleted = siteSessions.filter((session) => isSessionSectionComplete(session)).length;
          const siteIncomplete = Math.max(0, siteSessions.length - siteCompleted);
          const siteFlags = siteSessions.reduce((sum, session) => sum + Number(session.flaggedCount || 0), 0);
          const siteExposure = latestCycleExposureForSite(siteGroup);
          return (
          <section className={`mc-site-cycle-group ${siteCollapsed ? 'is-collapsed' : ''}`} key={siteGroup.siteName}>
            <div className="mc-site-cycle-heading">
              <button className="mc-site-collapse-button" type="button" onClick={() => toggleSiteCollapsed(siteGroup.siteName)} aria-expanded={!siteCollapsed}>
                <span className="mc-sessions-label">Site</span>
                <h2><span className="mc-site-disclosure" aria-hidden="true">{siteCollapsed ? '▸' : '▾'}</span>{siteGroup.siteName} <small>({siteGroup.cycles.length} cycle{siteGroup.cycles.length === 1 ? '' : 's'})</small></h2>
                <div className="mc-site-summary-strip" aria-label={`${siteGroup.siteName} summary`}>
                  <span><strong>{siteCompleted}</strong> completed</span>
                  <span><strong>{siteIncomplete}</strong> incomplete</span>
                  <span><strong>{siteFlags}</strong> flag{siteFlags === 1 ? '' : 's'}</span>
                  <span className={Number(siteExposure || 0) > 0 ? 'is-positive' : Number(siteExposure || 0) < 0 ? 'is-negative' : ''}><strong>{formatExposure(siteExposure)}</strong> exposure</span>
                </div>
              </button>
              <div className="mc-site-cycle-actions">
                {canFinalize &&
                siteGroup.cycles.length > 0 &&
                siteGroup.cycles.every((cycle) => {
                  const cycleSessions = Array.isArray(cycle?.sessions) ? cycle.sessions : [];
                  return cycleSessions.length > 0 && cycleSessions.every((session) => isSessionSectionComplete(session) && isSessionVerified(session));
                }) ? (
                  <button
                    className="mc-row-btn mc-finalize-all-cycles-btn"
                    type="button"
                    onClick={() => finalizeAllCycles(siteGroup)}
                    disabled={Boolean(finalizeBusyId)}
                  >
                    {finalizeBusyId === `site:${siteGroup.siteName}` ? "Finalizing…" : "Finalize All Cycles"}
                  </button>
                ) : null}
                {isAdmin ? (
                  <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => requestBulkArchive('site', siteGroup.siteName, siteGroup.cycles.flatMap((cycle) => cycle.sessions || []))} disabled={Boolean(archiveBusyId)}>
                    Archive Site
                  </button>
                ) : null}
              </div>
            </div>

            {!siteCollapsed ? siteGroup.cycles.map((cycle) => {
              const cycleSessions = cycle.sessions || [];
              const completedCount = cycleSessions.filter((item) => isSessionSectionComplete(item)).length;
              const cycleFlags = cycleSessions.reduce((sum, item) => sum + Number(item.flaggedCount || 0), 0);
              const cycleExposure = cycleSessions.reduce((sum, item) => sum + Number(item.costExposure || 0), 0);
              return (
                <div className="mc-cycle-card" key={`${siteGroup.siteName}-${cycle.cycleId}`}>
                  <div className="mc-cycle-card-head">
                    <div>
                      <h3>{cycle.cycleName}</h3>
                      {cycle.description ? <p>{cycle.description}</p> : <p>Cycle progress grouped by section ownership.</p>}
                    </div>
                    <div className="mc-cycle-summary">
                      <div className="mc-cycle-summary-metrics">
                        <span>{completedCount} / {cycleSessions.length} completed</span>
                        <strong className={Number(cycleExposure || 0) > 0 ? 'is-positive' : Number(cycleExposure || 0) < 0 ? 'is-negative' : ''}>{formatExposure(cycleExposure)}</strong>
                        <small>{cycleFlags} flag{cycleFlags === 1 ? '' : 's'}</small>
                      </div>
                      <div className="mc-cycle-action-bar">
                        {canFinalize && cycleSessions.length > 0 && completedCount === cycleSessions.length && cycleSessions.every(isSessionVerified) ? (
                          <button
                            className="mc-row-btn mc-cycle-finalize-btn"
                            type="button"
                            onClick={() => finalizeCycle({ ...cycle, sessions: cycleSessions })}
                            disabled={Boolean(finalizeBusyId)}
                          >
                            {finalizeBusyId === `cycle:${cycle.cycleId || cycle.cycleName}` ? "Finalizing…" : "Finalize Count"}
                          </button>
                        ) : null}
                        {isAdmin ? (
                          <button className="mc-row-btn mc-row-btn--ghost mc-cycle-archive-btn" type="button" onClick={() => requestBulkArchive('cycle', `${siteGroup.siteName} · ${cycle.cycleName}`, cycleSessions)} disabled={Boolean(archiveBusyId)}>
                            Archive Cycle
                          </button>
                        ) : null}

                      </div>
                      {compareStatus ? <small className="mc-cycle-compare-status">{compareStatus}</small> : null}
                    </div>
                  </div>

                  <div className="mc-cycle-session-table">
                    <div className="mc-cycle-session-head">
                      <span>Section</span>
                      <span>Status</span>
                      <span>Progress</span>
                      <span>Exposure</span>
                      <span>Counted By</span>
                      <span>Action</span>
                    </div>
                    {cycleSessions.map((session) => {
                      const progress = formatProgress(session);
                      const isCompleted = isSessionSectionComplete(session);
                      const isVerified = isSessionVerified(session);
                      const selectedCompareId = Array.from(compareSessionIds)[0];
                      const selectedCompareSession = selectedCompareId ? compareSessionById(selectedCompareId) : null;
                      const compareCompatibilityState = compareCompatibility(session, selectedCompareSession);
                      const compareChecked = compareSessionIds.has(session.sessionId);
                      const compareDisabled = !compareChecked && (compareSessionIds.size >= 2 || !compareCompatibilityState.allowed);
                      const compareTitle = compareChecked
                        ? "Remove from comparison"
                        : compareDisabled
                          ? compareCompatibilityState.reason || "Only two sessions can be compared"
                          : selectedCompareSession
                            ? `Compare with ${resolveSessionSectionName(selectedCompareSession)} — cycle/date may differ`
                            : "Select this section, then choose the same section from this site on any cycle or date";
                      return (
                        <div className="mc-cycle-session-row" key={session.sessionId} id={`mc-session-row-${encodeURIComponent(session.sessionId)}`} tabIndex={-1}>
                          <div className="mc-cycle-section-name">
                            <strong>{resolveSessionSectionName(session)}</strong>
                            <small>{session.sessionId}</small>
                            <small className="mc-session-category-scope">{resolveCategoryScopeLabel(session)} · {session.totalLines || 0} products</small>
                          </div>
                          <div>
                            <span className={`mc-session-status ${isCompleted ? 'completed' : String(session.status || 'active').toLowerCase()}`}>{formatSessionStatusLabel(session)}</span>
                            <span className={`mc-session-verification ${isVerified ? 'is-verified' : 'is-unverified'}`}>{isVerified ? 'Verified' : 'Unverified'}</span>
                            {(session?.correctedAt || (Array.isArray(session?.correctionVersions) && session.correctionVersions.length > 0)) ? (
                              <span className="mc-session-correction-badge" title={session?.correctedBy ? `Corrected by ${session.correctedBy}` : 'Corrected count'}>Correction</span>
                            ) : null}
                            {session?.restoredFromArchive ? (
                              <span className="mc-session-restored-badge" title={`Restored from archive${session?.restoreReason ? `: ${session.restoreReason}` : ''}`}>Restored</span>
                            ) : null}
                          </div>
                          <div className="mc-session-progress-wrap">
                            <div className="mc-session-progress-head">
                              <span>{session.completedLines || 0} / {session.totalLines || 0}</span>
                            </div>
                            <div className="mc-session-progress-bar">
                              <span style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                          <div className="mc-session-flags mc-cycle-exposure">
                            <strong className={Number(session.costExposure || 0) > 0 ? 'is-positive' : Number(session.costExposure || 0) < 0 ? 'is-negative' : ''}>{formatExposure(session.costExposure)}</strong>
                          </div>
                          <div className="mc-cycle-counted-by">
                            <strong>{session.startedBy || 'User'}</strong>
                            <small>{formatStartedAt(session.startedAt)}</small>
                          </div>
                          <div className="mc-session-actions">
                            <label className={`mc-session-compare-check ${compareDisabled ? "is-disabled" : ""}`} title={compareTitle}>
                              <input
                                type="checkbox"
                                checked={compareChecked}
                                disabled={compareDisabled}
                                onChange={() => toggleCompareSession(session.sessionId)}
                              />
                              <span>Compare</span>
                            </label>
                            {isAdmin && isCompleted && !isVerified ? (
                              <button
                                className="mc-row-btn mc-row-btn--verify"
                                type="button"
                                onClick={() => setConfirmVerify(session)}
                                disabled={Boolean(verifyBusyId)}
                              >
                                {verifyBusyId === session.sessionId ? 'Verifying…' : 'Verify'}
                              </button>
                            ) : null}
                            <Link
                              className={`mc-row-btn mc-link-btn ${!isCompleted ? 'mc-session-action-primary' : ''}`}
                              to={`/morpheus-count/session/${encodeURIComponent(session.sessionId || '')}`}
                              onPointerEnter={() => warmSessionDetail(session)}
                              onFocus={() => warmSessionDetail(session)}
                              onClick={() => beginOpenSession(session)}
                            >
                              {isCompleted ? 'View' : 'Continue'}
                            </Link>
                            {!isCompleted && isAdmin ? (
                              <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => setConfirmArchive(session)}>
                                Archive
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }) : null}
          </section>
          );
        })}
      </div>

      {openingSession ? (
        <div className="mc-session-openingOverlay" role="status" aria-live="polite">
          <div className="mc-session-openingCard">
            <div className="mc-session-openingSpinner" aria-hidden="true" />
            <div>
              <span className="mc-sessions-label">Opening live count</span>
              <h3>{openingSession.title || openingSession.sessionId}</h3>
              <p>Preparing the live count workspace. This usually takes a few seconds.</p>
              {openingSession.slow ? (
                <p className="mc-session-openingSlow">Still opening… if this does not change, refresh once or return to Sessions and try again.</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {confirmVerify ? (
        <div className="mc-sessions-confirmModal" role="dialog" aria-modal="true" aria-labelledby="mc-verify-title">
          <div className="mc-sessions-confirmModal__backdrop" onClick={() => { if (!verifyBusyId) setConfirmVerify(null); }} />
          <div className="mc-sessions-confirmModal__card mc-verify-confirm-card">
            <div className="mc-sessions-label">Confirm verification</div>
            <h3 id="mc-verify-title">Verify {resolveSessionSectionName(confirmVerify)}?</h3>
            <p>You are confirming that this completed count has been checked and is ready for finalization.</p>
            <div className="mc-verify-confirm-summary">
              <span><small>Site</small><strong>{resolveSessionSiteName(confirmVerify)}</strong></span>
              <span><small>Cycle</small><strong>{resolveSessionCycle(confirmVerify)?.cycleName || 'Cycle'}</strong></span>
              <span><small>Exposure</small><strong className={Number(confirmVerify?.costExposure || 0) > 0 ? 'is-positive' : Number(confirmVerify?.costExposure || 0) < 0 ? 'is-negative' : ''}>{formatExposure(confirmVerify?.costExposure)}</strong></span>
              <span><small>Flags</small><strong>{Number(confirmVerify?.flaggedCount || 0)}</strong></span>
            </div>
            <div className="mc-sessions-confirmModal__actions">
              <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => setConfirmVerify(null)} disabled={Boolean(verifyBusyId)}>Cancel</button>
              <button className="mc-row-btn mc-row-btn--verify" type="button" onClick={() => verifySession(confirmVerify)} disabled={Boolean(verifyBusyId)}>
                {verifyBusyId ? 'Verifying…' : 'Confirm Verification'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmFinalize ? (
        <div className="mc-sessions-confirmModal" role="dialog" aria-modal="true">
          <div
            className="mc-sessions-confirmModal__backdrop"
            onClick={() => setConfirmFinalize(null)}
          />
          <div className="mc-sessions-confirmModal__card">
            <div className="mc-sessions-label">{confirmFinalize?.type === "blocked" ? "Cycles not ready" : confirmFinalize?.type === "day" ? "Confirm day finalization" : confirmFinalize?.type === "site" ? "Confirm all cycle finalization" : confirmFinalize?.type === "cycle" ? "Confirm cycle finalization" : "Finalize completed count"}</div>
            <h3>{["blocked", "cycle", "site", "day"].includes(confirmFinalize?.type) ? confirmFinalize.title : `Finalize ${confirmFinalize.sessionId}?`}</h3>
            <p>
              {["blocked", "cycle", "site", "day"].includes(confirmFinalize?.type)
                ? confirmFinalize.message
                : "This will remove the completed session from Sessions and move it to Archive under completed sessions."}
            </p>
            {finalizeBusyId ? <p className="mc-sessions-confirmModal__status">Finalizing count…</p> : null}
            <div className="mc-sessions-confirmModal__actions">
              <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => setConfirmFinalize(null)} disabled={Boolean(finalizeBusyId)}>
                {confirmFinalize?.type === "blocked" ? "Close" : "Cancel"}
              </button>
              {confirmFinalize?.type === "blocked" ? null : confirmFinalize?.type === "day" ? (
                <button className="mc-row-btn mc-row-btn--primary mc-cycle-finalize-btn" type="button" onClick={executeFinalizeDay} disabled={Boolean(finalizeBusyId)}>
                  {finalizeBusyId ? "Finalizing…" : "Confirm Finalize Day"}
                </button>
              ) : confirmFinalize?.type === "site" ? (
                <button className="mc-row-btn mc-row-btn--primary mc-cycle-finalize-btn" type="button" onClick={() => executeFinalizeAllCycles(confirmFinalize.siteGroup)} disabled={Boolean(finalizeBusyId)}>
                  {finalizeBusyId ? "Finalizing…" : "Confirm Finalize All Cycles"}
                </button>
              ) : confirmFinalize?.type === "cycle" ? (
                <button className="mc-row-btn mc-row-btn--primary mc-cycle-finalize-btn" type="button" onClick={() => executeFinalizeCycle(confirmFinalize.cycle)} disabled={Boolean(finalizeBusyId)}>
                  {finalizeBusyId ? "Finalizing…" : "Confirm Finalize Cycle"}
                </button>
              ) : (
                <button className="mc-row-btn mc-row-btn--primary" type="button" onClick={() => finalizeRecord(confirmFinalize.sessionId)} disabled={Boolean(finalizeBusyId)}>
                  {finalizeBusyId ? "Finalizing…" : "Finalize Count"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {(archiveBusyId || finalizeBusyId) && !confirmArchive && !confirmFinalize ? (
        <div className="mc-sessions-confirmModal" role="dialog" aria-modal="true">
          <div className="mc-sessions-confirmModal__backdrop" />
          <div className="mc-sessions-confirmModal__card">
            <div className="mc-sessions-label">Archiving session</div>
            <h3>MC is moving this session to Archives…</h3>
            <p className="mc-sessions-confirmModal__status">Saving final snapshot and updating the shared database.</p>
          </div>
        </div>
      ) : null}

      {bulkArchiveTarget ? (
        <div className="mc-sessions-confirmModal" role="dialog" aria-modal="true">
          <div className="mc-sessions-confirmModal__backdrop" onClick={() => { if (!archiveBusyId) setBulkArchiveTarget(null); }} />
          <div className="mc-sessions-confirmModal__card">
            <div className="mc-sessions-label">Bulk archive</div>
            <h3>Archive {bulkArchiveTarget.label}?</h3>
            <p>This will move {bulkArchiveTarget.sessions.length} session{bulkArchiveTarget.sessions.length === 1 ? '' : 's'} from the active workspace into Archives. The audit history and count detail remain preserved.</p>
            <div className="mc-sessions-confirmModal__actions">
              <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => setBulkArchiveTarget(null)} disabled={Boolean(archiveBusyId)}>Cancel</button>
              <button className="mc-row-btn mc-row-btn--primary" type="button" onClick={executeBulkArchive} disabled={Boolean(archiveBusyId)}>{archiveBusyId ? 'Archiving…' : 'Confirm Archive All'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmArchive ? (
        <div className="mc-sessions-confirmModal" role="dialog" aria-modal="true">
          <div
            className="mc-sessions-confirmModal__backdrop"
            onClick={() => { if (!archiveBusyId) setConfirmArchive(null); }}
          />
          <div className="mc-sessions-confirmModal__card">
            <div className="mc-sessions-label">Archive session</div>
            <h3>Archive {confirmArchive.sessionId}?</h3>
            <p>This will remove the session from In Progress and move it to Archive.</p>
            {archiveBusyId ? <p className="mc-sessions-confirmModal__status">Archiving session…</p> : null}
            <div className="mc-sessions-confirmModal__actions">
              <button className="mc-row-btn mc-row-btn--ghost" type="button" onClick={() => setConfirmArchive(null)} disabled={Boolean(archiveBusyId)}>
                Cancel
              </button>
              <button className="mc-row-btn mc-row-btn--primary" type="button" onClick={() => archiveRecord(confirmArchive.sessionId)} disabled={Boolean(archiveBusyId)}>
                {archiveBusyId ? "Archiving…" : "Archive Session"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}

export default function MorpheusCountSessions() {
  const location = useLocation();
  const pathname = String(location?.pathname || "").replace(/\/$/, "");

  // Safety bridge: in rare cases React Router can update the URL from
  // /sessions to /session/:id while the Sessions component remains mounted.
  // When that happens, render the live-count detail directly instead of
  // leaving the user stuck on the Sessions screen. This does not change the
  // route table or tab navigation behaviour.
  if (/^\/morpheus-count\/session\/[^/]+$/.test(pathname)) {
    return <MorpheusCountSessionDetail />;
  }

  return <MorpheusCountSessionsInner />;
}
